class PreviewerController {
  constructor() {
    this.currentStep = 2; // Start at Kid Information step
    this.formData = {
      photo: null,
      childName: ''
    };
    this.storyData = null; // Store the story data from API
    this.storySlug = null; // Store the story slug from URL
    this.faceApiLoaded = false;
    this.generationId = null; // Store the generation ID
    this.uploadedImageUrl = null; // Store the uploaded image URL
    this.productFirstImageUrl = null; // Store the product's first image URL

    // Track processed scenes for paired thumbnails
    this.processedScenes = new Set();
    this.scenePairs = new Map(); // Map to track which scenes belong to which pairs
    this.sceneLoaderIntervals = new Map(); // Track countdown intervals per scene

    // Performance optimization flags
    this.initializationComplete = false;
    this.pusherInitialized = false;

    // Face cropping configuration
    // Adjust this value to control crop margins:
    // 0.3 = tight crop (face + hair + small neck), 0.8 = generous crop with more background
    this.faceCropMarginFactor = 0.4; // 40% margin around face for tighter crop

    // Initialize with performance optimizations
    this.init();
  }

  // Helper method to detect language from current URL handle
  isArabicLanguage() {
    const currentUrl = window.location.pathname;
    return currentUrl.includes('-ar');
  }

  async init() {
    try {
      // Check for generation_id parameter first
      const generationId = this.getUrlParameter('generation_id');
      if (generationId) {
        // Skip to step 3 and load generated scenes
        await this.handleGenerationIdParameter(generationId);
        return;
      }

      // Setup event listeners immediately for better UX
      this.setupEventListeners();

      // Start loading Face API in background (non-blocking)
      this.loadFaceAPIInBackground();

      // Load critical APIs in parallel
      const storyLoadPromise = this.loadStoryFromURL();
      const productDataPromise = this.loadProductDataInBackground();

      // Wait only for the critical APIs (story data and GraphQL)
      await Promise.all([storyLoadPromise, productDataPromise]);

      // Hide page loader after critical APIs are loaded
      this.hidePageLoader();

      this.initializationComplete = true;

    } catch (error) {
      console.error('❌ Error initializing previewer:', error);
      this.showErrorOverlay();
    }
  }

  async handleGenerationIdParameter(generationId) {
    try {
      // Validate generation ID format
      if (!generationId || typeof generationId !== 'string' || generationId.length < 10) {
        console.error('❌ Invalid generation ID format:', generationId);
        this.showErrorOverlay();
        return;
      }

      // Set the generation ID
      this.generationId = generationId;

      // Check if this generation ID exists in local storage, if not add it
      this.checkAndAddGenerationToLocalStorage(generationId);

      // Fetch generated scenes data
      const generatedData = await this.fetchGeneratedScenes(generationId);

      if (generatedData) {
        // Persist generated data for later fallbacks
        this.generatedData = generatedData;
        // Update form data with kid name and image
        this.formData.childName = generatedData.kid_name || '';
        this.uploadedImageUrl = this.normalizeApiUrl(generatedData.kid_image);

        // Store story data for navigation
        this.storyData = this.convertGeneratedScenesToStoryData(generatedData.generated_scenes);

        // Get story slug from URL or generation data
        let storySlug = this.getUrlParameter('story');
        if (!storySlug && generatedData.story_slug) {
          storySlug = generatedData.story_slug;
        }

        if (storySlug) {
          this.storySlug = this.normalizeProductHandle(storySlug);
          // Fetch product data to get variant ID for cart operations (background load)
          this.loadProductDataInBackground();
        }


        // Update page title immediately when data is available
        this.updatePageTitle(generatedData);

        // Update the generation in local storage with complete data
        this.updateGenerationInLocalStorage(generationId, generatedData);

        // Skip directly to step 3
        this.currentStep = 3;

        // Setup event listeners
        this.setupEventListeners();

        // Initialize Pusher only when needed
        this.initializePusherIfNeeded();

        // Update step navigation to show step 3 as active
        this.updateStepNavigation();

        // Show step 3 content
        this.showStep3Content();

        // Populate scenes with generated data
        this.populateGeneratedScenes(generatedData);

        // Hide page loader
        this.hidePageLoader();

      } else {
        console.error('❌ Failed to fetch generated scenes data');
        this.showErrorOverlay();
      }

    } catch (error) {
      console.error('❌ Error handling generation ID parameter:', error);
      this.showErrorOverlay();
    }
  }

  async fetchGeneratedScenes(generationId) {
    try {
      const response = await fetch(`https://api.bysnaps.ai/preview-stories/previewes/shopify/${generationId}`);

      if (!response.ok) {
        console.error(`❌ HTTP error! status: ${response.status}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        return data.data;
      } else {
        console.error('❌ Invalid generated scenes data received:', data);
        return null;
      }

    } catch (error) {
      console.error('❌ Error fetching generated scenes:', error);
      return null;
    }
  }

  convertGeneratedScenesToStoryData(generatedScenes) {
    try {
      if (!generatedScenes || !Array.isArray(generatedScenes)) {
        return [];
      }

      return generatedScenes.map(scene => {
        // Handle different scene structures
        const imageUrl = scene.processedImageUrl || scene.sceneUrl || '';
        const isBlurred = scene.sceneBlurred === 'true' || scene.blurred === true || scene.status === 'blurred';
        const sceneType = scene.type || scene.sceneType || 'Scene';

        return {
          scene_url: imageUrl,
          type: sceneType,
          blurred: isBlurred,
          order: parseInt(scene.sceneOrder) || 0
        };
      });

    } catch (error) {
      console.error('❌ Error converting generated scenes to story data:', error);
      return [];
    }
  }

  getFullImageUrl(imageUrl) {
    try {
      if (!imageUrl) {
        return '';
      }

      // If the URL already starts with http/https, return as is
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      }

      // If it's a relative URL, prefix with the API base URL
      if (imageUrl.startsWith('/')) {
        return `https://api.bysnaps.ai${imageUrl}`;
      }

      // If it doesn't start with /, add it
      return `https://api.bysnaps.ai/${imageUrl}`;

    } catch (error) {
      console.error('❌ Error getting full image URL:', error);
      return imageUrl || '';
    }
  }

  async getVariantIdFromGeneration() {
    try {
      // Get the story slug from the generation data
      const storySlug = this.storySlug;
      if (!storySlug) {
        console.error('❌ No story slug available in generation data');
        return null;
      }

      // Normalize the handle and also compute a human name fallback
      const normalizedHandle = this.normalizeProductHandle(storySlug);
      const humanStoryName = this.deriveStoryNameFromSlug(normalizedHandle);

      // Try direct product URL first (using normalized handle)
      const directProductUrl = canonicalUrl(`products/${normalizedHandle}`);

      let productResponse = await fetch(`${directProductUrl}.js`);

      if (!productResponse.ok) {
        // Fallback to search if direct product not found
        let searchResponse = await fetch(canonicalUrl(`search?q=${encodeURIComponent(normalizedHandle)}&type=product`));

        if (!searchResponse.ok) {
          console.error('❌ Search failed:', searchResponse.status);
          // Try searching by human-readable story name (spaces) as a secondary fallback
          if (humanStoryName) {
            searchResponse = await fetch(canonicalUrl(`search?q=${encodeURIComponent(humanStoryName)}&type=product`));
            if (!searchResponse.ok) {
              console.error('❌ Search by story name failed:', searchResponse.status);
              return null;
            }
          } else {
            return null;
          }
        }

        const searchData = await searchResponse.text();

        const parser = new DOMParser();
        const searchDoc = parser.parseFromString(searchData, 'text/html');

        // Find the product URL from search results
        let productLink = searchDoc.querySelector(`a[href*="/products/${normalizedHandle}"]`);
        if (!productLink) {
          // As a generic fallback, pick the first product link
          productLink = searchDoc.querySelector('a[href*="/products/"]');
        }
        if (!productLink) {
          console.error('❌ Product not found in search results');
          return null;
        }

        const productUrl = productLink.href;

        // Construct the correct product JSON URL
        const productJsonUrl = productUrl.replace(/\/$/, '') + '.js';

        productResponse = await fetch(productJsonUrl);
      }

      if (!productResponse.ok) {
        console.error('❌ Failed to get product data:', productResponse.status);
        return null;
      }

      // Check if product response is JSON
      const productContentType = productResponse.headers.get('content-type');

      let productData;
      try {
        productData = await productResponse.json();
      } catch (error) {
        console.error('❌ Product data error - invalid JSON response');
        return null;
      }

      // Get the first available variant
      const availableVariant = productData.variants.find(variant => variant.available);
      if (!availableVariant) {
        console.error('❌ No available variants found');
        return null;
      }

      return availableVariant.id;

    } catch (error) {
      console.error('❌ Error getting variant ID from generation:', error);
      return null;
    }
  }

  async checkIfGenerationInCart() {
    try {
      if (!this.generationId) {
        return false;
      }

      // Get current cart state
      const response = await fetch(localeUrl('cart.js'));

      if (!response.ok) {
        console.error('❌ Failed to get cart data');
        return false;
      }

      const cartData = await response.json();

      // Check if any item in cart has the same Generation ID
      const isInCart = cartData.items.some(item => {
        if (item.properties && item.properties['Generation ID']) {
          return item.properties['Generation ID'] === this.generationId;
        }
        return false;
      });

      if (isInCart) {
        this.showToaster(window.themeStrings.previewer.alreadyInCart, 'info');
      }

      return isInCart;

    } catch (error) {
      console.error('❌ Error checking if generation in cart:', error);
      return false;
    }
  }

  showStep3Content() {
    try {
      // Hide all step content
      const stepContents = document.querySelectorAll('.step-content');
      stepContents.forEach(content => {
        content.classList.remove('active');
        content.style.display = 'none';
      });

      // Show step 3 content
      const step3Content = document.getElementById('step-3');
      if (step3Content) {
        step3Content.style.display = 'block';
        setTimeout(() => {
          step3Content.classList.add('active');
        }, 50);
      }

    } catch (error) {
      console.error('❌ Error showing step 3 content:', error);
    }
  }

  populateGeneratedScenes(generatedData) {
    try {
      if (!generatedData.generated_scenes || !Array.isArray(generatedData.generated_scenes)) {
        console.error('❌ No generated scenes data available');
        return;
      }

      // Update story header with kid name
      const storyHeaderText = document.getElementById('storyHeaderText');
      if (storyHeaderText && generatedData.kid_name) {
        storyHeaderText.textContent = themeFmt(window.themeStrings.previewer.previewTitleNamed, { name: generatedData.kid_name });
      }

      // Update testimonial images
      this.updateTestimonialImages(generatedData);

      // Generate thumbnails and scenes with processed images
      this.generateProcessedSceneThumbnails(generatedData.generated_scenes);
      this.generateProcessedSceneDisplay(generatedData.generated_scenes);

      // Update additional story CTA
      this.updateAdditionalStoryCTA();

      // Update page title
      this.updatePageTitle(generatedData);

      // Start share tooltip timer for generation_id URLs
      this.startShareTooltipTimer();

    } catch (error) {
      console.error('❌ Error populating generated scenes:', error);
    }
  }

  updateTestimonialImages(generatedData) {
    try {
      // Update testimonial before image (kid photo)
      if (generatedData.kid_image) {
        const testimonialBeforeImage = document.querySelector('.testimonial-image.before img');
        if (testimonialBeforeImage) {
          testimonialBeforeImage.src = generatedData.kid_image;
          testimonialBeforeImage.alt = window.themeStrings.previewer.kidPhotoAlt;
        }
      }

      // Update testimonial after image: prefer product's first image if available, otherwise fall back to generated cover
      const testimonialAfterImage = document.querySelector('.testimonial-image.after img');
      if (testimonialAfterImage) {
        if (this.productFirstImageUrl) {
          testimonialAfterImage.src = this.productFirstImageUrl;
          testimonialAfterImage.alt = window.themeStrings.previewer.productImageAlt;
        } else if (generatedData.generated_scenes && generatedData.generated_scenes.length > 0) {
          const firstScene = generatedData.generated_scenes.find(scene => scene.sceneOrder === '0');
          if (firstScene) {
            const imageUrl = firstScene.processedImageUrl || firstScene.sceneUrl || '';
            if (imageUrl) {
              testimonialAfterImage.src = this.getFullImageUrl(imageUrl);
              testimonialAfterImage.alt = window.themeStrings.previewer.storyCoverAlt;
            }
          }
        }
      }

    } catch (error) {
      console.error('❌ Error updating testimonial images:', error);
    }
  }

  updatePageTitle(generatedData) {
    try {
      // Get kid name from API data
      const kidName = generatedData.kid_name;

      // Get story name from API data
      let storyName = window.themeStrings.previewer.storyFallback;
      if (generatedData.story_name) {
        storyName = generatedData.story_name;
      }

      // Update page title
      if (kidName && storyName) {
        document.title = themeFmt(window.themeStrings.previewer.pageTitle, { kid: kidName, story: storyName });
      }

    } catch (error) {
      console.error('❌ Error updating page title:', error);
    }
  }

  generateProcessedSceneThumbnails(generatedScenes) {
    const thumbnailsContainer = document.getElementById('sceneThumbnails');
    if (!thumbnailsContainer) {
      return;
    }

    if (generatedScenes.length === 0) {
      thumbnailsContainer.innerHTML = `
        <div class="thumbnail-placeholder">
          <p>${window.themeStrings.previewer.noScenes}</p>
        </div>
      `;
      return;
    }

    let thumbnailsHTML = '';
    let sceneCounter = 1;

    // Sort scenes by sceneOrder
    const sortedScenes = generatedScenes.sort((a, b) => parseInt(a.sceneOrder) - parseInt(b.sceneOrder));

    // Helper function to get image URL from scene
    const getSceneImageUrl = (scene) => {
      return scene.processedImageUrl || scene.sceneUrl || '';
    };

    // Helper function to check if scene is blurred
    const isSceneBlurred = (scene) => {
      return scene.sceneBlurred === 'true' || scene.blurred === true || scene.status === 'blurred';
    };

    // Step 1: Add cover scene first (sceneOrder "0")
    const coverScene = sortedScenes.find(scene => scene.sceneOrder === '0');
    if (coverScene) {
      const coverImageUrl = this.getFullImageUrl(getSceneImageUrl(coverScene));
      thumbnailsHTML += `
        <div class="scene-thumbnail single-thumbnail active" data-scene-index="0" onclick="previewerController.scrollToScene(0)">
          <img src="${coverImageUrl}" alt="${window.themeStrings.previewer.cover}" loading="lazy">
          <div class="scene-thumbnail-overlay">
            <span class="scene-number">${window.themeStrings.previewer.cover}</span>
          </div>
        </div>
      `;
    }

    // Step 2: Collect non-blurred middle scenes and join in pairs
    const middleScenes = sortedScenes.filter(scene =>
      scene.sceneOrder !== '0' &&
      scene.sceneOrder !== (sortedScenes.length - 1).toString() &&
      !isSceneBlurred(scene)
    );

    // Join non-blurred middle scenes in pairs
    for (let i = 0; i < middleScenes.length; i += 2) {
      const currentScene = middleScenes[i];
      const nextScene = middleScenes[i + 1];

      if (nextScene) {
        // Pair of scenes
        const currentImageUrl = this.getFullImageUrl(getSceneImageUrl(currentScene));
        const nextImageUrl = this.getFullImageUrl(getSceneImageUrl(nextScene));
        thumbnailsHTML += `
          <div class="thumbnail-row" data-scene-index="${currentScene.sceneOrder}" onclick="previewerController.scrollToScene(${currentScene.sceneOrder})">
            <div class="scene-thumbnail paired-thumbnail">
              <img src="${currentImageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: sceneCounter })}" loading="lazy">
            </div>
            <div class="scene-thumbnail paired-thumbnail">
              <img src="${nextImageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: sceneCounter + 1 })}" loading="lazy">
            </div>
            <div class="scene-thumbnail-overlay">
              <span class="scene-number">${sceneCounter}</span>
            </div>
          </div>
        `;
        sceneCounter++;
      } else {
        // Single scene
        const currentImageUrl = this.getFullImageUrl(getSceneImageUrl(currentScene));
        thumbnailsHTML += `
          <div class="scene-thumbnail single-thumbnail" data-scene-index="${currentScene.sceneOrder}" onclick="previewerController.scrollToScene(${currentScene.sceneOrder})">
            <img src="${currentImageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: sceneCounter })}" loading="lazy">
            <div class="scene-thumbnail-overlay">
              <span class="scene-number">${sceneCounter}</span>
            </div>
          </div>
        `;
        sceneCounter++;
      }
    }

    // Step 3: Add blurred scenes as individual
    const blurredScenes = sortedScenes.filter(scene => isSceneBlurred(scene));
    blurredScenes.forEach(scene => {
      // Skip if it's cover or back scene
      if (scene.sceneOrder !== '0' && scene.sceneOrder !== (sortedScenes.length - 1).toString()) {
        const imageUrl = this.getFullImageUrl(getSceneImageUrl(scene));
        thumbnailsHTML += `
          <div class="scene-thumbnail single-thumbnail blurred-thumbnail" data-scene-index="${scene.sceneOrder}" onclick="previewerController.scrollToScene(${scene.sceneOrder})">
            <img src="${imageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: sceneCounter })}" loading="lazy">
            <div class="scene-thumbnail-overlay">
              <span class="scene-number">${sceneCounter}</span>
            </div>
          </div>
        `;
        sceneCounter++;
      }
    });

    // Step 4: Add back scene last
    const backScene = sortedScenes.find(scene => scene.sceneOrder === (sortedScenes.length - 1).toString());
    if (backScene) {
      const backImageUrl = this.getFullImageUrl(getSceneImageUrl(backScene));
      thumbnailsHTML += `
        <div class="scene-thumbnail single-thumbnail" data-scene-index="${backScene.sceneOrder}" onclick="previewerController.scrollToScene(${backScene.sceneOrder})">
          <img src="${backImageUrl}" alt="${window.themeStrings.previewer.back}" loading="lazy">
          <div class="scene-thumbnail-overlay">
            <span class="scene-number">${window.themeStrings.previewer.back}</span>
          </div>
        </div>
      `;
    }

    thumbnailsContainer.innerHTML = thumbnailsHTML;
  }

  generateProcessedSceneDisplay(generatedScenes) {
    const scenesContainer = document.getElementById('scenesContainer');
    if (!scenesContainer) {
      return;
    }

    if (generatedScenes.length === 0) {
      scenesContainer.innerHTML = `
        <div class="scene-placeholder">
          <div class="placeholder-content">
            <h3>${window.themeStrings.previewer.noStoryScenesTitle}</h3>
            <p>${window.themeStrings.previewer.noStoryScenesText}</p>
          </div>
        </div>
      `;
      return;
    }

    let scenesHTML = '';

    // Sort scenes by sceneOrder
    const sortedScenes = generatedScenes.sort((a, b) => parseInt(a.sceneOrder) - parseInt(b.sceneOrder));

    // Helper function to get image URL from scene
    const getSceneImageUrl = (scene) => {
      return scene.processedImageUrl || scene.sceneUrl || '';
    };

    // Helper function to check if scene is blurred
    const isSceneBlurred = (scene) => {
      return scene.sceneBlurred === 'true' || scene.blurred === true || scene.status === 'blurred';
    };

    // Step 1: Add cover scene first (sceneOrder "0")
    const coverScene = sortedScenes.find(scene => scene.sceneOrder === '0');
    if (coverScene) {
      const coverImageUrl = this.getFullImageUrl(getSceneImageUrl(coverScene));
      scenesHTML += `
        <div class="scene-item single-scene" data-scene-index="0" id="scene-0">
          <div class="scene-image">
            <img src="${coverImageUrl}" alt="${window.themeStrings.previewer.cover}" loading="lazy">
          </div>
        </div>
      `;
    }

    // Step 2: Collect non-blurred middle scenes and join in pairs
    const middleScenes = sortedScenes.filter(scene =>
      scene.sceneOrder !== '0' &&
      scene.sceneOrder !== (sortedScenes.length - 1).toString() &&
      !isSceneBlurred(scene)
    );

    // Join non-blurred middle scenes in pairs
    for (let i = 0; i < middleScenes.length; i += 2) {
      const currentScene = middleScenes[i];
      const nextScene = middleScenes[i + 1];

      if (nextScene) {
        // Pair of scenes
        const currentImageUrl = this.getFullImageUrl(getSceneImageUrl(currentScene));
        const nextImageUrl = this.getFullImageUrl(getSceneImageUrl(nextScene));
        scenesHTML += `
          <div class="scene-row">
            <div class="scene-item paired-scene" data-scene-index="${currentScene.sceneOrder}" id="scene-${currentScene.sceneOrder}">
              <div class="scene-image">
                <img src="${currentImageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: currentScene.sceneOrder })}" loading="lazy">
              </div>
            </div>
            <div class="scene-item paired-scene" data-scene-index="${nextScene.sceneOrder}" id="scene-${nextScene.sceneOrder}">
              <div class="scene-image">
                <img src="${nextImageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: nextScene.sceneOrder })}" loading="lazy">
              </div>
            </div>
          </div>
        `;
      } else {
        // Single scene
        const currentImageUrl = this.getFullImageUrl(getSceneImageUrl(currentScene));
        scenesHTML += `
          <div class="scene-item single-scene" data-scene-index="${currentScene.sceneOrder}" id="scene-${currentScene.sceneOrder}">
            <div class="scene-image">
              <img src="${currentImageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: currentScene.sceneOrder })}" loading="lazy">
            </div>
          </div>
        `;
      }
    }

    // Step 3: Add blurred scenes as individual
    const blurredScenes = sortedScenes.filter(scene => isSceneBlurred(scene));
    blurredScenes.forEach(scene => {
      // Skip if it's cover or back scene
      if (scene.sceneOrder !== '0' && scene.sceneOrder !== (sortedScenes.length - 1).toString()) {
        const imageUrl = this.getFullImageUrl(getSceneImageUrl(scene));
        const overlayText = document.querySelector('.previewer-container')?.dataset.blurredOverlayText || '';
        scenesHTML += `
          <div class="scene-item single-scene blurred-scene" data-scene-index="${scene.sceneOrder}" id="scene-${scene.sceneOrder}">
            <div class="scene-image">
              <img src="${imageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: scene.sceneOrder })}" loading="lazy">
              <div class="scene-overlay-info">
                <div class="scene-overlay-icon" aria-hidden="true"><i class="fas fa-info-circle"></i></div>
                <div class="scene-overlay-text">${overlayText}</div>
              </div>
            </div>
          </div>
        `;
      }
    });

    // Step 4: Add back scene last
    const backScene = sortedScenes.find(scene => scene.sceneOrder === (sortedScenes.length - 1).toString());
    if (backScene) {
      const backImageUrl = this.getFullImageUrl(getSceneImageUrl(backScene));
      scenesHTML += `
        <div class="scene-item single-scene back-cover" data-scene-index="${backScene.sceneOrder}" id="scene-${backScene.sceneOrder}">
          <div class="scene-image">
            <img src="${backImageUrl}" alt="${window.themeStrings.previewer.back}" loading="lazy">
          </div>
        </div>
      `;
    }

    scenesContainer.innerHTML = scenesHTML;
  }

  loadFaceAPIInBackground() {
    // Start loading Face API in background without blocking

    this.initializeFaceApi().then(() => {

    }).catch(error => {
      console.warn('⚠️ Face API background loading failed:', error);
      // Don't throw error - this is non-critical
    });
  }

  isFaceAPIReady() {
    return this.faceApiLoaded;
  }

  async loadProductDataInBackground() {
    try {
      const storySlugParam = this.getUrlParameter('story');
      const normalizedStorySlug = this.normalizeProductHandle(storySlugParam || this.storySlug);
      if (normalizedStorySlug) {
        await this.fetchProductData(normalizedStorySlug);
        // Update testimonial images after product data is loaded
        this.updateTestimonialBeforeImage();
        this.updateTestimonialAfterImageFromProduct();

        // Set up a retry mechanism in case testimonial image didn't load
        this.setupTestimonialImageRetry();
      }
    } catch (error) {
      console.error('❌ Error loading product data in background:', error);
      // Don't throw error - continue without product data
    }
  }

  setupTestimonialImageRetry() {
    // Retry updating testimonial image every 2 seconds for up to 10 seconds
    let retryCount = 0;
    const maxRetries = 5;

    const retryInterval = setInterval(() => {
      const testimonialBeforeImage = document.querySelector('.testimonial-image.before img');

      if (testimonialBeforeImage && testimonialBeforeImage.src && !testimonialBeforeImage.src.includes('data:') && !testimonialBeforeImage.src.includes('placeholder')) {

        clearInterval(retryInterval);
        return;
      }

      retryCount++;
      if (retryCount >= maxRetries) {
        console.warn('⚠️ Testimonial before image retry limit reached');
        clearInterval(retryInterval);
        return;
      }


      this.updateTestimonialBeforeImage();
    }, 2000);
  }

  async initializeFaceApi() {
    // Only initialize if not already loaded
    if (this.faceApiLoaded) {
      return;
    }

    try {


      // Lazy load Face API library first
      const faceapi = await window.loadFaceAPI();



      // Load the models from GitHub raw content with timeout
      const MODEL_URL = 'https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights';

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error(window.themeStrings.previewer.faceApiTimeout)), 30000); // 30 second timeout
      });

      const loadPromise = Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
        faceapi.nets.ageGenderNet.loadFromUri(MODEL_URL)
      ]);

      await Promise.race([loadPromise, timeoutPromise]);

      this.faceApiLoaded = true;

    } catch (error) {
      console.error('❌ Error loading face-api.js models:', error);
      this.faceApiLoaded = false;
      throw error;
    }
  }

  async initializePusherIfNeeded() {
    // Only initialize Pusher if not already initialized and we're in step 3
    if (this.pusherInitialized || this.currentStep !== 3) {
      return;
    }

    try {


      // Lazy load Pusher library first
      const Pusher = await window.loadPusher();

      // Initialize Pusher
      this.pusher = new Pusher('45e734cbf0eb7fcc3b2a', {
        cluster: 'eu',
        useTLS: true
      });

      // Setup Pusher subscriptions
      this.setupPusherSubscriptions();

      this.pusherInitialized = true;

    } catch (error) {
      console.error('❌ Error initializing Pusher:', error);
    }
  }

  async validateFace(imageElement) {
    if (!this.faceApiLoaded) {
      throw new Error(window.themeStrings.previewer.faceModelsNotLoaded);
    }

    // Marks errors meant for the user so the catch below can tell them apart from analysis failures.
    const userError = (message) => Object.assign(new Error(message), { isUserFacing: true });

    try {
      // Detect faces with landmarks and age/gender
      const detections = await faceapi
        .detectAllFaces(imageElement, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withAgeAndGender();

      // Check if exactly one face is detected
      if (detections.length === 0) {
        throw userError(window.themeStrings.previewer.noFace);
      }

      if (detections.length > 1) {
        throw userError(window.themeStrings.previewer.multipleFaces);
      }

      const detection = detections[0];

      // More lenient face detection confidence (lowered from 0.7 to 0.4)
      if (detection.detection.score < 0.4) {
        throw userError(window.themeStrings.previewer.lowConfidence);
      }

      // More flexible age check - increased from 25 to 35 and made it a warning instead of error
      const estimatedAge = Math.round(detection.age);
      if (estimatedAge > 35) {
        console.warn(`Age estimated as ${estimatedAge}, but allowing photo to proceed`);
      }

      // Log successful validation with more encouraging message
      return {
        success: true,
        age: estimatedAge,
        confidence: detection.detection.score,
        gender: detection.gender,
        faceBox: detection.detection.box // Include face bounding box for cropping
      };

    } catch (error) {
      if (error.isUserFacing) {
        throw error;
      }
      // More lenient error handling - don't fail for analysis errors
      console.warn('Face analysis had minor issues, but proceeding:', error.message);
      return {
        success: true,
        age: 8, // Default age if analysis fails
        confidence: 0.5, // Default confidence
        gender: 'unknown',
        faceBox: null
      };
    }
  }

  async cropImageAroundFace(imageElement, faceBox, marginFactor = 0.8) {
    return new Promise((resolve, reject) => {
      try {
        // Validate inputs
        if (!imageElement || !faceBox) {
          throw new Error('Invalid image element or face box');
        }

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        // Calculate crop dimensions with margin for hair and face
        const faceWidth = faceBox.width;
        const faceHeight = faceBox.height;

        // Validate face dimensions
        if (faceWidth <= 0 || faceHeight <= 0) {
          throw new Error('Invalid face dimensions');
        }

        // Add focused margins for face, hair, and head with small neck portion
        const marginX = faceWidth * marginFactor;
        const marginY = faceHeight * marginFactor;

        // Calculate crop area with optimized margins for tighter crop
        let cropX = Math.max(0, faceBox.x - marginX * 0.8); // Reduced horizontal margin
        let cropY = Math.max(0, faceBox.y - marginY * 1.5); // Increased top margin for better hair and forehead capture
        let cropWidth = Math.min(imageElement.width - cropX, faceWidth + (marginX * 1.6)); // Tighter width
        let cropHeight = Math.min(imageElement.height - cropY, faceHeight + (marginY * 2.3)); // Adjusted height to accommodate increased top margin

        // Ensure we don't exceed image boundaries
        if (cropX + cropWidth > imageElement.width) {
          cropWidth = imageElement.width - cropX;
        }
        if (cropY + cropHeight > imageElement.height) {
          cropHeight = imageElement.height - cropY;
        }

        // Validate final crop dimensions
        if (cropWidth <= 0 || cropHeight <= 0) {
          throw new Error('Invalid crop dimensions calculated');
        }

        // Set canvas size to crop dimensions
        canvas.width = cropWidth;
        canvas.height = cropHeight;

        // Draw the cropped portion
        ctx.drawImage(
          imageElement,
          cropX, cropY, cropWidth, cropHeight, // Source rectangle
          0, 0, cropWidth, cropHeight // Destination rectangle
        );

        // Convert canvas to blob
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        }, 'image/jpeg', 0.9);

      } catch (error) {
        reject(error);
      }
    });
  }

  getUrlParameter(name) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
  }

  normalizeApiUrl(value) {
    if (!value) return '';
    try {
      return new URL(value, 'https://api.bysnaps.ai').toString();
    } catch {
      return value;
    }
  }

  // Normalize a Shopify product handle:
  // - trim whitespace
  // - lower-case
  // - remove a trailing "-short" suffix if present
  normalizeProductHandle(handle) {
    try {
      if (!handle || typeof handle !== 'string') return handle;
      let normalized = handle.trim().toLowerCase();
      // Strip known suffixes like -short, -gift, -premium from the end of the slug
      const suffixes = ['-short', '-gift', '-premium'];
      let modified = true;
      while (modified) {
        modified = false;
        for (const suffix of suffixes) {
          if (normalized.endsWith(suffix)) {
            normalized = normalized.slice(0, -suffix.length);
            modified = true;
          }
        }
      }
      return normalized;
    } catch (error) {
      console.warn('⚠️ Failed to normalize product handle:', error);
      return handle;
    }
  }

  // Convert a human story name to a Shopify-like handle
  toHandleFromName(name) {
    try {
      if (!name || typeof name !== 'string') return null;
      return name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    } catch (error) {
      console.warn('⚠️ Failed to convert story name to handle:', error);
      return null;
    }
  }

  // Derive a human-friendly story name from a slug/handle (e.g. "fire-fighter" -> "fire fighter")
  deriveStoryNameFromSlug(slug) {
    try {
      if (!slug || typeof slug !== 'string') return null;
      return slug.replace(/-/g, ' ').trim();
    } catch (error) {
      console.warn('⚠️ Failed to derive story name from slug:', error);
      return null;
    }
  }

  async loadStoryFromURL() {
    try {
      const storySlugParam = this.getUrlParameter('story');
      const normalizedStorySlug = this.normalizeProductHandle(storySlugParam);

      if (!normalizedStorySlug) {
        this.showErrorOverlay();
        return;
      }

      this.storySlug = normalizedStorySlug;



      // Fetch story data from API (critical path)
      await this.fetchStoryData(normalizedStorySlug);



    } catch (error) {
      console.error('❌ Error loading story from URL:', error);
      this.showErrorOverlay();
    }
  }

  async fetchStoryData(slug) {
    try {


      const response = await fetch(`https://api.bysnaps.ai/previewer-scenes/shopify/story/${slug}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        this.storyData = data.data;

        // Update the cover image in the first step
        this.updateCoverImage();

        // Update testimonial before image if product data is already loaded
        this.updateTestimonialBeforeImage();

        // Populate story scenes in step 3
        this.populateStoryScenes();



      } else {
        console.error('❌ Invalid story data received:', data);
        this.showErrorOverlay();
      }

    } catch (error) {
      console.error('❌ Error fetching story data:', error);
      this.showErrorOverlay();
    }
  }

  async fetchProductData(slug) {
    try {
      // Add caching to prevent duplicate requests
      if (this.productMetafields && this.productVariantId) {

        return;
      }



      // GraphQL query to fetch kid_image, story_title, and language metafields
      const query = `
        query getProduct($handle: String!, $language: LanguageCode!) @inContext(language: $language) {
          product(handle: $handle) {
            title
            handle
            id
            images(first: 1) {
              edges {
                node {
                  url
                  altText
                  width
                  height
                }
              }
            }
            variants(first: 1) {
              edges {
                node {
                  id
                  title
                  availableForSale
                }
              }
            }

            kidImage: metafield(namespace: "custom", key: "kid_image") {
              type
              reference {
                ... on MediaImage {
                  image {
                    url
                    altText
                    width
                    height
                  }
                }
              }
            }
            
            storyTitle: metafield(namespace: "custom", key: "story_title") {
              value
            }
            
            language: metafield(namespace: "custom", key: "language") {
              value
            }
          }
        }
      `;

      const normalizedHandle = this.normalizeProductHandle(slug);
      const variables = { handle: normalizedHandle, language: storefrontLanguage() };

      // Use the actual Shopify store domain
      const shopDomain = 'bysnaps.myshopify.com';

      // Try to get the storefront access token from meta tags
      let storefrontAccessToken = null;

      // Check for token in meta tags
      const tokenMeta = document.querySelector('meta[name="shopify-storefront-api-token"]');
      if (tokenMeta) {
        storefrontAccessToken = tokenMeta.getAttribute('content');
      }

      // Check for token in window object (if set by theme)
      if (!storefrontAccessToken && window.Shopify && window.Shopify.storefrontAccessToken) {
        storefrontAccessToken = window.Shopify.storefrontAccessToken;
      }

      // Check for token in theme settings (if available)
      if (!storefrontAccessToken && window.theme && window.theme.storefrontAccessToken) {
        storefrontAccessToken = window.theme.storefrontAccessToken;
      }

      if (!storefrontAccessToken) {
        console.warn('❌ Storefront Access Token not found. Please add it to your theme.');
        return;
      }

      // Use the correct GraphQL endpoint URL
      const graphqlUrl = `https://${shopDomain}/api/2024-04/graphql.json`;

      const requestBody = JSON.stringify({
        query: query.trim(),
        variables: variables
      });

      const response = await fetch(graphqlUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
          'Accept': 'application/json'
        },
        body: requestBody,
        mode: 'cors',
        credentials: 'omit'
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();

      if (data.errors) {
        console.error('❌ GraphQL Errors:', data.errors);
        data.errors.forEach((error, index) => {
          console.error(`Error ${index + 1}:`, error.message);
          if (error.locations) {
            console.error('  Locations:', error.locations);
          }
          if (error.path) {
            console.error('  Path:', error.path);
          }
        });
        // Try a fallback: attempt fetching by a handle derived from story name
        const fallbackHandle = this.toHandleFromName(this.generatedData?.story_name || this.deriveStoryNameFromSlug(this.storySlug));
        if (fallbackHandle && fallbackHandle !== this.storySlug) {
          const fallbackVariables = { handle: this.normalizeProductHandle(fallbackHandle), language: storefrontLanguage() };
          const fallbackRequestBody = JSON.stringify({ query: query.trim(), variables: fallbackVariables });
          const fallbackResponse = await fetch(graphqlUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
              'Accept': 'application/json'
            },
            body: fallbackRequestBody,
            mode: 'cors',
            credentials: 'omit'
          });
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            if (fallbackData && fallbackData.data && fallbackData.data.product) {
              data = fallbackData;
            } else {
              return;
            }
          } else {
            return;
          }
        } else {
          return;
        }
      }

      if (data.data && data.data.product) {
        const product = data.data.product;

        // Extract kid_image metafield from GraphQL response
        let kidImageUrl = null;
        let productImageUrl = null;

        if (product.kidImage && product.kidImage.reference && product.kidImage.reference.image) {
          kidImageUrl = product.kidImage.reference.image.url;
        }

        // Extract first product image
        if (product.images && product.images.edges && product.images.edges.length > 0) {
          const firstImageNode = product.images.edges[0].node;
          productImageUrl = firstImageNode?.url || null;
        }

        // Store product metafields
        this.productMetafields = {
          kid_image: kidImageUrl,
          story_title: product.storyTitle?.value || product.title,
          story_description: null,
          language: product.language?.value || null
        };

        // Store first image URL for testimonial after image
        this.productFirstImageUrl = productImageUrl;

        // Check if language is Arabic and setup validation (based on URL handle)
        if (this.isArabicLanguage()) {
          this.setupArabicValidation();
        } else {
          // Setup English/Latin validation for non-Arabic languages
          this.setupEnglishValidation();
        }

        // Update name input placeholder and validation if Arabic
        this.updateNameInputForLanguage();

        // Extract variant ID from the first variant
        if (product.variants && product.variants.edges && product.variants.edges.length > 0) {
          const variant = product.variants.edges[0].node;
          this.productVariantId = variant.id.split('/').pop(); // Extract the numeric ID from the GraphQL ID
        }



        // Update testimonial before image now that product data is available
        this.updateTestimonialBeforeImage();
        // Update testimonial after image from product
        this.updateTestimonialAfterImageFromProduct();
      }

    } catch (error) {
      console.error('❌ Error fetching product data via GraphQL:', error);
      // Don't show error overlay for product data fetch failure
      // Just log it and continue with default testimonial image
    }
  }

  updateCoverImage() {
    try {
      // If product first image is available, prefer it and skip updating cover
      if (this.productFirstImageUrl) {
        return;
      }
      // Find the cover scene (type: "cover")
      const coverScene = this.storyData.find(scene => scene.type === 'cover');

      if (!coverScene) {
        console.error('No cover scene found in story data');
        return;
      }

      // Find the testimonial after image container
      const testimonialAfterImage = document.querySelector('.testimonial-image.after img');

      if (testimonialAfterImage) {
        // Construct the full image URL
        const imageUrl = `https://api.bysnaps.ai${coverScene.scene_url}`;
        testimonialAfterImage.src = imageUrl;
        testimonialAfterImage.alt = window.themeStrings.previewer.storyCoverAlt;

      } else {
        console.error('Testimonial after image element not found');
      }

    } catch (error) {
      console.error('Error updating cover image:', error);
    }
  }

  updateTestimonialBeforeImage() {
    try {
      // Get the product metafield for kid image from fetched data
      const kidImageMetafield = this.productMetafields?.kid_image;

      if (kidImageMetafield) {
        const testimonialBeforeImage = document.querySelector('.testimonial-image.before img');

        if (testimonialBeforeImage) {
          testimonialBeforeImage.src = kidImageMetafield;
          testimonialBeforeImage.alt = window.themeStrings.previewer.kidPhotoAlt;

        } else {
          console.error('❌ Testimonial before image element not found');
        }
      } else {
        console.warn('⚠️ No kid image metafield available yet');
      }
    } catch (error) {
      console.error('❌ Error updating testimonial before image:', error);
    }
  }

  populateStoryScenes() {
    try {
      if (!this.storyData || this.storyData.length === 0) {
        return;
      }

      // Generate thumbnails
      this.generateSceneThumbnails();

      // Generate scene display
      this.generateSceneDisplay();

      // Start share tooltip timer
      this.startShareTooltipTimer();

    } catch (error) {
      console.error('Error populating story scenes:', error);
    }
  }

  generateSceneThumbnails() {
    const thumbnailsContainer = document.getElementById('sceneThumbnails');
    if (!thumbnailsContainer) {
      return;
    }

    if (this.storyData.length === 0) {
      thumbnailsContainer.innerHTML = `
        <div class="thumbnail-placeholder">
          <p>${window.themeStrings.previewer.noScenes}</p>
        </div>
      `;
      return;
    }

    let thumbnailsHTML = '';
    let sceneCounter = 1; // Counter for numbering after cover - start from 1

    // Step 1: Add cover scene first (single, not joined)
    // Cover scene is always Order "0"
    const coverScene = this.storyData[0];
    const coverImageUrl = `https://api.bysnaps.ai${coverScene.scene_url}`;
    thumbnailsHTML += `
      <div class="scene-thumbnail single-thumbnail active" data-scene-index="0" onclick="previewerController.scrollToScene(0)">
        <img src="${coverImageUrl}" alt="${window.themeStrings.previewer.cover}" loading="lazy">
        <div class="scene-thumbnail-overlay">
          <span class="scene-number">${window.themeStrings.previewer.cover}</span>
        </div>
      </div>
    `;

    // Step 2: Collect non-blurred middle scenes (not cover, not back) and join in pairs
    const middleScenes = this.storyData.slice(1, -1); // Exclude cover and back
    const nonBlurredMiddleScenes = middleScenes.filter(scene => scene.blurred !== true);

    // Join non-blurred middle scenes in pairs
    for (let i = 0; i < nonBlurredMiddleScenes.length; i += 2) {
      const currentScene = nonBlurredMiddleScenes[i];
      const nextScene = nonBlurredMiddleScenes[i + 1];
      const currentImageUrl = `https://api.bysnaps.ai${currentScene.scene_url}`;

      if (nextScene) {
        // Pair of scenes
        const nextImageUrl = `https://api.bysnaps.ai${nextScene.scene_url}`;
        thumbnailsHTML += `
          <div class="thumbnail-row" data-scene-index="${this.storyData.indexOf(currentScene)}" onclick="previewerController.scrollToScene(${this.storyData.indexOf(currentScene)})">
            <div class="scene-thumbnail paired-thumbnail">
              <img src="${currentImageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: sceneCounter })}" loading="lazy">
            </div>
            <div class="scene-thumbnail paired-thumbnail">
              <img src="${nextImageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: sceneCounter + 1 })}" loading="lazy">
            </div>
            <div class="scene-thumbnail-overlay">
              <span class="scene-number">${sceneCounter}</span>
            </div>
          </div>
        `;
        sceneCounter++;
      } else {
        // Single scene (odd number of non-blurred middle scenes)
        thumbnailsHTML += `
          <div class="scene-thumbnail single-thumbnail" data-scene-index="${this.storyData.indexOf(currentScene)}" onclick="previewerController.scrollToScene(${this.storyData.indexOf(currentScene)})">
            <img src="${currentImageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: sceneCounter })}" loading="lazy">
            <div class="scene-thumbnail-overlay">
              <span class="scene-number">${sceneCounter}</span>
            </div>
          </div>
        `;
        sceneCounter++;
      }
    }

    // Step 3: Add blurred scenes from story as individual
    const blurredScenes = this.storyData.filter(scene => scene.blurred === true);
    blurredScenes.forEach(scene => {
      const sceneIndex = this.storyData.indexOf(scene);
      const imageUrl = `https://api.bysnaps.ai${scene.scene_url}`;

      // Skip if it's cover or back scene (already handled)
      if (sceneIndex !== 0 && sceneIndex !== this.storyData.length - 1) {
        thumbnailsHTML += `
          <div class="scene-thumbnail single-thumbnail blurred-thumbnail" data-scene-index="${sceneIndex}" onclick="previewerController.scrollToScene(${sceneIndex})">
            <img src="${imageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: sceneCounter })}" loading="lazy">
            <div class="scene-thumbnail-overlay">
              <span class="scene-number">${sceneCounter}</span>
            </div>
          </div>
        `;
        sceneCounter++;
      }
    });

    // Step 4: Add back scene last (single, not joined)
    const backScene = this.storyData[this.storyData.length - 1];
    const backImageUrl = `https://api.bysnaps.ai${backScene.scene_url}`;
    thumbnailsHTML += `
      <div class="scene-thumbnail single-thumbnail" data-scene-index="${this.storyData.length - 1}" onclick="previewerController.scrollToScene(${this.storyData.length - 1})">
        <img src="${backImageUrl}" alt="${window.themeStrings.previewer.back}" loading="lazy">
        <div class="scene-thumbnail-overlay">
          <span class="scene-number">${window.themeStrings.previewer.back}</span>
        </div>
      </div>
    `;

    thumbnailsContainer.innerHTML = thumbnailsHTML;
  }

  generateSceneDisplay() {
    const scenesContainer = document.getElementById('scenesContainer');
    if (!scenesContainer) {
      return;
    }

    if (this.storyData.length === 0) {
      scenesContainer.innerHTML = `
        <div class="scene-placeholder">
          <div class="placeholder-content">
            <h3>${window.themeStrings.previewer.noStoryScenesTitle}</h3>
            <p>${window.themeStrings.previewer.noStoryScenesText}</p>
          </div>
        </div>
      `;
      return;
    }

    let scenesHTML = '';

    // Step 1: Add cover scene first (single, not joined)
    // Cover scene is always Order "0"
    const coverScene = this.storyData[0];
    const coverImageUrl = `https://api.bysnaps.ai${coverScene.scene_url}`;
    scenesHTML += `
      <div class="scene-item single-scene" data-scene-index="0" id="scene-0">
        <div class="scene-image">
          <img src="${coverImageUrl}" alt="${window.themeStrings.previewer.cover}" loading="lazy">
        </div>
      </div>
    `;

    // Step 2: Collect non-blurred middle scenes (not cover, not back) and join in pairs
    const middleScenes = this.storyData.slice(1, -1); // Exclude cover and back
    const nonBlurredMiddleScenes = middleScenes.filter(scene => scene.blurred !== true);

    // Join non-blurred middle scenes in pairs
    for (let i = 0; i < nonBlurredMiddleScenes.length; i += 2) {
      const currentScene = nonBlurredMiddleScenes[i];
      const nextScene = nonBlurredMiddleScenes[i + 1];
      const currentImageUrl = `https://api.bysnaps.ai${currentScene.scene_url}`;

      if (nextScene) {
        // Pair of scenes
        const nextImageUrl = `https://api.bysnaps.ai${nextScene.scene_url}`;
        scenesHTML += `
          <div class="scene-row">
            <div class="scene-item paired-scene" data-scene-index="${this.storyData.indexOf(currentScene)}" id="scene-${this.storyData.indexOf(currentScene)}">
              <div class="scene-image">
                <img src="${currentImageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: this.storyData.indexOf(currentScene) })}" loading="lazy">
              </div>
            </div>
            <div class="scene-item paired-scene" data-scene-index="${this.storyData.indexOf(nextScene)}" id="scene-${this.storyData.indexOf(nextScene)}">
              <div class="scene-image">
                <img src="${nextImageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: this.storyData.indexOf(nextScene) })}" loading="lazy">
              </div>
            </div>
          </div>
        `;
      } else {
        // Single scene (odd number of non-blurred middle scenes)
        scenesHTML += `
          <div class="scene-item single-scene" data-scene-index="${this.storyData.indexOf(currentScene)}" id="scene-${this.storyData.indexOf(currentScene)}">
            <div class="scene-image">
              <img src="${currentImageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: this.storyData.indexOf(currentScene) })}" loading="lazy">
            </div>
          </div>
        `;
      }
    }

    // Step 3: Add blurred scenes from story as individual
    const blurredScenes = this.storyData.filter(scene => scene.blurred === true);
    blurredScenes.forEach(scene => {
      const sceneIndex = this.storyData.indexOf(scene);
      const imageUrl = `https://api.bysnaps.ai${scene.scene_url}`;

      // Skip if it's cover or back scene (already handled)
      if (sceneIndex !== 0 && sceneIndex !== this.storyData.length - 1) {
        const overlayText = document.querySelector('.previewer-container')?.dataset.blurredOverlayText || '';
        scenesHTML += `
          <div class="scene-item single-scene blurred-scene" data-scene-index="${sceneIndex}" id="scene-${sceneIndex}">
            <div class="scene-image">
              <img src="${imageUrl}" alt="${themeFmt(window.themeStrings.previewer.sceneAlt, { number: sceneIndex })}" loading="lazy">
              <div class="scene-overlay-info">
                <div class="scene-overlay-icon" aria-hidden="true"><i class="fas fa-info-circle"></i></div>
                <div class="scene-overlay-text">${overlayText}</div>
              </div>
            </div>
          </div>
        `;
      }
    });

    // Step 4: Add back scene last (single, not joined)
    const backScene = this.storyData[this.storyData.length - 1];
    const backImageUrl = `https://api.bysnaps.ai${backScene.scene_url}`;
    scenesHTML += `
      <div class="scene-item single-scene back-cover" data-scene-index="${this.storyData.length - 1}" id="scene-${this.storyData.length - 1}">
        <div class="scene-image">
          <img src="${backImageUrl}" alt="${window.themeStrings.previewer.back}" loading="lazy">
        </div>
      </div>
    `;

    scenesContainer.innerHTML = scenesHTML;

    // Add loaders to non-blurred scenes except back covers
    this.addLoadersToScenes();
  }

  addLoadersToScenes() {
    try {
      // Reset processed scenes tracking
      this.processedScenes.clear();
      this.scenePairs.clear();

      // Add loader to cover scene (scene 0)
      this.addSceneLoader(0);
      this.addThumbnailLoader(0);

      // Add loaders to non-blurred middle scenes (not back cover)
      const middleScenes = this.storyData.slice(1, -1); // Exclude cover and back
      const nonBlurredMiddleScenes = middleScenes.filter(scene => scene.blurred !== true);

      // Track scene pairs
      for (let i = 0; i < nonBlurredMiddleScenes.length; i += 2) {
        const currentScene = nonBlurredMiddleScenes[i];
        const nextScene = nonBlurredMiddleScenes[i + 1];
        const currentSceneIndex = this.storyData.indexOf(currentScene);

        if (nextScene) {
          // This is a pair
          const nextSceneIndex = this.storyData.indexOf(nextScene);
          this.scenePairs.set(currentSceneIndex, nextSceneIndex);
          this.scenePairs.set(nextSceneIndex, currentSceneIndex);

          this.addSceneLoader(currentSceneIndex);
          this.addSceneLoader(nextSceneIndex);
          this.addThumbnailLoader(currentSceneIndex); // Only add to first scene in pair
        } else {
          // Single scene
          this.addSceneLoader(currentSceneIndex);
          this.addThumbnailLoader(currentSceneIndex);
        }
      }

    } catch (error) {
      console.error('❌ Error adding loaders to scenes:', error);
    }
  }

  scrollToScene(sceneIndex) {
    // Update active thumbnail - handle both single thumbnails and thumbnail rows
    const thumbnails = document.querySelectorAll('.scene-thumbnail.single-thumbnail, .thumbnail-row');
    thumbnails.forEach((thumb) => {
      const thumbSceneIndex = parseInt(thumb.dataset.sceneIndex);
      thumb.classList.toggle('active', thumbSceneIndex === sceneIndex);
    });

    // Scroll to scene
    const sceneElement = document.getElementById(`scene-${sceneIndex}`);
    if (sceneElement) {
      sceneElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  setupEventListeners() {
    // Photo upload event listeners
    const uploadSection = document.querySelector('.upload-section');
    if (uploadSection) {
      uploadSection.addEventListener('click', (e) => {
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.click();
        }
      });

      // Drag and drop event listeners
      uploadSection.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.stopPropagation();
        uploadSection.classList.add('drag-over');
      });

      uploadSection.addEventListener('dragenter', (e) => {
        e.preventDefault();
        e.stopPropagation();
        uploadSection.classList.add('drag-over');
      });

      uploadSection.addEventListener('dragleave', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only remove drag-over if we're leaving the upload section entirely
        if (!uploadSection.contains(e.relatedTarget)) {
          uploadSection.classList.remove('drag-over');
        }
      });

      uploadSection.addEventListener('drop', (e) => {
        e.preventDefault();
        e.stopPropagation();
        uploadSection.classList.remove('drag-over');

        const files = e.dataTransfer.files;
        if (files.length > 0) {
          const file = files[0];

          // Validate file type before processing
          if (
            !['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'].includes(file.type)
            && !/\.(?:jpe?g|png|webp|heic|heif)$/i.test(file.name || '')
          ) {
            this.showToaster(window.themeStrings.previewer.dropInvalidFile, 'error');
            return;
          }

          // Create a mock event object for handlePhotoUpload
          const mockEvent = {
            target: {
              files: [file]
            }
          };
          this.handlePhotoUpload(mockEvent);
        }
      });
    }

    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.addEventListener('change', (e) => {
        this.handlePhotoUpload(e);
      });
    }

    // Name input event listener
    const nameInput = document.querySelector('input[name="child-name"]');
    if (nameInput) {
      // Set maxlength attribute
      nameInput.setAttribute('maxlength', '15');

      // Set placeholder based on current theme language
      const placeholder = this.getChildNamePlaceholder();
      nameInput.setAttribute('placeholder', placeholder);

      nameInput.addEventListener('input', (e) => {
        this.handleNameChange(e);
      });
    }

    // Setup Arabic validation if needed (based on URL handle)
    if (this.isArabicLanguage()) {
      this.setupArabicValidation();
    }

    // Preview button event listener
    const previewBtn = document.querySelector('.preview-btn');
    if (previewBtn) {
      previewBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.handlePreviewClick();
      });
    }

    // Step navigation event listeners - DISABLED
    // const stepItems = document.querySelectorAll('.step-item');
    // stepItems.forEach(item => {
    //   item.addEventListener('click', (e) => {
    //     const step = parseInt(item.dataset.step);
    //     if (step) {
    //       this.goToStep(step);
    //     }
    //   });
    // });
  }

  setupPusherSubscriptions() {
    try {
      // Subscribe to the Previewer channel
      const channel = this.pusher.subscribe('Previewer');

      // Listen for all events on the Previewer channel
      channel.bind_global((eventName, data) => {
        // Check if this event is for our current generation
        if (this.generationId && eventName === this.generationId) {
          this.handleGenerationUpdate(data);
        }
      });

      // Also listen for the generic "generation_id" event as fallback
      channel.bind('generation_id', (data) => {
        this.handleGenerationUpdate(data);
      });

    } catch (error) {
      console.error('❌ Error setting up Pusher subscriptions:', error);
    }
  }

  handleGenerationUpdate(data) {
    try {
      const { sceneOrder, type, processedImageUrl } = data;

      // Handle scene processing updates
      if (type === 'Scene' && sceneOrder && processedImageUrl) {
        // Update the corresponding scene in the story display
        this.updateSceneImage(parseInt(sceneOrder), processedImageUrl);

        // Track processed scene
        this.processedScenes.add(parseInt(sceneOrder));

        // Update scene thumbnails if needed
        this.updateSceneThumbnail(parseInt(sceneOrder), processedImageUrl);

      } else if (type === 'Cover' && processedImageUrl) {
        // Update cover image
        this.updateCoverImageWithProcessed(processedImageUrl);
      } else if (type === 'Back' && processedImageUrl) {
        // Update back cover image
        this.updateBackCoverImage(processedImageUrl);
        this.updateSceneThumbnail(this.storyData.length - 1, processedImageUrl);
      }

    } catch (error) {
      console.error('❌ Error handling generation update:', error);
    }
  }

  updateSceneImage(sceneOrder, processedImageUrl) {
    try {
      // Find the scene element by ID (scene-{sceneOrder})
      const sceneElement = document.getElementById(`scene-${sceneOrder}`);

      if (!sceneElement) {
        console.warn(`⚠️ Scene element not found for scene-${sceneOrder}`);
        return;
      }

      const sceneImage = sceneElement.querySelector('.scene-image img');
      if (!sceneImage) {
        console.warn(`⚠️ Scene image not found in scene-${sceneOrder}`);
        return;
      }

      // Use preloading to prevent old image flash during loading
      this.preloadAndUpdateSceneImage(sceneImage, processedImageUrl, themeFmt(window.themeStrings.previewer.processedSceneAlt, { number: sceneOrder }), sceneOrder);

    } catch (error) {
      console.error(`❌ Error updating scene ${sceneOrder} image:`, error);
    }
  }

  // Helper method to preload scene image and update with loading state
  preloadAndUpdateSceneImage(imgElement, newSrc, altText, sceneOrder) {
    return new Promise((resolve, reject) => {
      try {
        // Create a new image to preload
        const preloadImg = new Image();

        preloadImg.onload = () => {
          // Once preloaded, update the actual image
          imgElement.src = newSrc;
          imgElement.alt = altText;

          // Remove loading state using existing loader method
          this.removeSceneLoader(sceneOrder);

          resolve();
        };

        preloadImg.onerror = () => {
          console.error(`❌ Failed to preload scene image for scene ${sceneOrder}:`, newSrc);

          // Remove loading state even on error
          this.removeSceneLoader(sceneOrder);

          reject(new Error('Failed to preload scene image'));
        };

        // Start preloading
        preloadImg.src = newSrc;

      } catch (error) {
        console.error(`❌ Error in preloadAndUpdateSceneImage for scene ${sceneOrder}:`, error);
        reject(error);
      }
    });
  }

  addSceneLoader(sceneOrder) {
    try {
      const sceneElement = document.getElementById(`scene-${sceneOrder}`);

      if (sceneElement) {
        // Check if loader already exists
        const existingLoader = sceneElement.querySelector('.scene-loader');
        if (existingLoader) {
          return; // Loader already exists
        }

        // Create loader overlay with spinner
        const loader = document.createElement('div');
        loader.className = 'scene-loader';
        loader.dataset.sceneOrder = String(sceneOrder);
        loader.innerHTML = `
          <div class="scene-loader-content">
            <div class="scene-spinner">
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
            </div>
            <p class="scene-loader-countdown">${themeFmt(window.themeStrings.previewer.loaderCountdown, { seconds: '<span class="countdown-number">55</span>' })}</p>
          </div>
        `;

        // Add loader to scene
        sceneElement.appendChild(loader);
      } else {
        console.warn(`⚠️ Scene element not found for adding loader to scene-${sceneOrder}`);
      }
    } catch (error) {
      console.error(`❌ Error adding loader to scene ${sceneOrder}:`, error);
    }
  }

  // Start countdown for a single scene loader after API response
  startSceneLoaderCountdown(sceneOrder, durationSeconds = 55) {
    try {
      const sceneElement = document.getElementById(`scene-${sceneOrder}`);
      if (!sceneElement) return;
      const loader = sceneElement.querySelector('.scene-loader');
      if (!loader) return;
      const countdownEl = loader.querySelector('.countdown-number');
      if (!countdownEl) return;

      // Clear any existing interval
      const existingIntervalId = this.sceneLoaderIntervals.get(sceneOrder);
      if (existingIntervalId) {
        clearInterval(existingIntervalId);
        this.sceneLoaderIntervals.delete(sceneOrder);
      }

      const endTime = Date.now() + durationSeconds * 1000;
      const updateTimer = () => {
        const msRemaining = endTime - Date.now();
        const secondsRemaining = Math.max(0, Math.ceil(msRemaining / 1000));
        countdownEl.textContent = String(secondsRemaining);
        if (secondsRemaining <= 0) {
          const id = this.sceneLoaderIntervals.get(sceneOrder);
          if (id) {
            clearInterval(id);
            this.sceneLoaderIntervals.delete(sceneOrder);
          }
        }
      };
      updateTimer();
      const intervalId = setInterval(updateTimer, 1000);
      this.sceneLoaderIntervals.set(sceneOrder, intervalId);
    } catch (error) {
      console.error('❌ Error starting scene loader countdown:', error);
    }
  }

  // Start countdowns for all current scene loaders (called after process API response)
  startAllSceneLoaderCountdowns(durationSeconds = 55) {
    try {
      const loaders = document.querySelectorAll('.scene-item .scene-loader');
      loaders.forEach((loaderEl) => {
        const parent = loaderEl.closest('.scene-item');
        let sceneOrder = null;
        if (loaderEl.dataset && loaderEl.dataset.sceneOrder) {
          sceneOrder = parseInt(loaderEl.dataset.sceneOrder);
        } else if (parent && parent.id && parent.id.startsWith('scene-')) {
          const idxStr = parent.id.replace('scene-', '');
          sceneOrder = parseInt(idxStr);
        }
        if (!Number.isNaN(sceneOrder)) {
          this.startSceneLoaderCountdown(sceneOrder, durationSeconds);
        }
      });
    } catch (error) {
      console.error('❌ Error starting all scene loader countdowns:', error);
    }
  }

  addThumbnailLoader(sceneOrder) {
    try {
      const thumbnailElement = document.querySelector(`[data-scene-index="${sceneOrder}"]`);

      if (thumbnailElement) {
        // Check if loader already exists
        const existingLoader = thumbnailElement.querySelector('.thumbnail-loader');
        if (existingLoader) {
          return; // Loader already exists
        }

        // Create loader overlay with spinner
        const loader = document.createElement('div');
        loader.className = 'thumbnail-loader';
        loader.innerHTML = `
          <div class="thumbnail-loader-content">
            <div class="thumbnail-spinner">
              <div class="spinner-ring"></div>
              <div class="spinner-ring"></div>
            </div>
          </div>
        `;

        // Add loader to thumbnail
        thumbnailElement.appendChild(loader);
      } else {
        console.warn(`⚠️ Thumbnail element not found for adding loader to scene-${sceneOrder}`);
      }
    } catch (error) {
      console.error(`❌ Error adding loader to thumbnail for scene ${sceneOrder}:`, error);
    }
  }

  removeSceneLoader(sceneOrder) {
    try {
      const sceneElement = document.getElementById(`scene-${sceneOrder}`);

      if (sceneElement) {
        const loader = sceneElement.querySelector('.scene-loader');
        if (loader) {
          // Clear countdown interval if active
          const intervalId = this.sceneLoaderIntervals.get(sceneOrder);
          if (intervalId) {
            clearInterval(intervalId);
            this.sceneLoaderIntervals.delete(sceneOrder);
          }
          loader.remove();
        }
      } else {
        console.warn(`⚠️ Scene element not found for removing loader from scene-${sceneOrder}`);
      }
    } catch (error) {
      console.error(`❌ Error removing loader from scene ${sceneOrder}:`, error);
    }
  }

  removeThumbnailLoader(sceneOrder) {
    try {
      const thumbnailElement = document.querySelector(`[data-scene-index="${sceneOrder}"]`);

      if (thumbnailElement) {
        const loader = thumbnailElement.querySelector('.thumbnail-loader');
        if (loader) {
          loader.remove();
        }
      } else {
        console.warn(`⚠️ Thumbnail element not found for removing loader from scene-${sceneOrder}`);
      }
    } catch (error) {
      console.error(`❌ Error removing loader from thumbnail for scene ${sceneOrder}:`, error);
    }
  }

  updateSceneThumbnail(sceneOrder, processedImageUrl) {
    try {
      // Find the thumbnail element by data attribute
      const thumbnailElement = document.querySelector(`[data-scene-index="${sceneOrder}"]`);

      if (!thumbnailElement) {
        console.warn(`⚠️ Thumbnail element not found for scene ${sceneOrder}`);
        return;
      }

      const thumbnailImage = thumbnailElement.querySelector('img');
      if (!thumbnailImage) {
        console.warn(`⚠️ Thumbnail image not found for scene ${sceneOrder}`);
        return;
      }

      // Use preloading to prevent old image flash during loading
      this.preloadAndUpdateThumbnailImage(thumbnailImage, processedImageUrl, themeFmt(window.themeStrings.previewer.processedSceneAlt, { number: sceneOrder }), sceneOrder);

    } catch (error) {
      console.error(`❌ Error updating scene ${sceneOrder} thumbnail:`, error);
    }
  }

  // Helper method to preload thumbnail image and update with loading state
  preloadAndUpdateThumbnailImage(imgElement, newSrc, altText, sceneOrder) {
    return new Promise((resolve, reject) => {
      try {
        // Create a new image to preload
        const preloadImg = new Image();

        preloadImg.onload = () => {
          // Once preloaded, update the actual image
          imgElement.src = newSrc;
          imgElement.alt = altText;

          // Check if this scene is part of a pair
          const pairedScene = this.scenePairs.get(parseInt(sceneOrder));
          if (pairedScene) {
            // This scene is part of a pair
            if (this.processedScenes.has(pairedScene)) {
              // Both scenes in the pair are processed, remove thumbnail loader
              const firstSceneInPair = Math.min(parseInt(sceneOrder), pairedScene);
              this.removeThumbnailLoader(firstSceneInPair);
            }
          } else {
            // This is a single scene, remove thumbnail loader immediately
            this.removeThumbnailLoader(parseInt(sceneOrder));
          }

          resolve();
        };

        preloadImg.onerror = () => {
          console.error(`❌ Failed to preload thumbnail image for scene ${sceneOrder}:`, newSrc);

          // Remove loading state even on error
          this.removeThumbnailLoader(sceneOrder);

          reject(new Error('Failed to preload thumbnail image'));
        };

        // Start preloading
        preloadImg.src = newSrc;

      } catch (error) {
        console.error(`❌ Error in preloadAndUpdateThumbnailImage for scene ${sceneOrder}:`, error);
        reject(error);
      }
    });
  }

  updateCoverImageWithProcessed(processedImageUrl) {
    try {
      // If product first image is available, prefer it and skip updating cover
      if (this.productFirstImageUrl) {
        return;
      }

      const testimonialAfterImage = document.querySelector('.testimonial-image.after img');

      if (!testimonialAfterImage) {
        console.warn('⚠️ Testimonial after image element not found');
        return;
      }

      // Solution 1: Preload image before updating src to prevent old image flash
      this.preloadAndUpdateImage(testimonialAfterImage, processedImageUrl, window.themeStrings.previewer.processedStoryCoverAlt);

    } catch (error) {
      console.error('❌ Error updating cover image:', error);
    }
  }

  // Helper method to preload image and update with loading state
  preloadAndUpdateImage(imgElement, newSrc, altText) {
    return new Promise((resolve, reject) => {
      try {
        // Create a new image to preload
        const preloadImg = new Image();

        preloadImg.onload = () => {
          // Once preloaded, update the actual image
          imgElement.src = newSrc;
          imgElement.alt = altText;

          // Remove loading state using existing loader method
          this.removeSceneLoader(0);

          resolve();
        };

        preloadImg.onerror = () => {
          console.error('❌ Failed to preload image:', newSrc);

          // Remove loading state even on error
          this.removeSceneLoader(0);

          reject(new Error('Failed to preload image'));
        };

        // Start preloading
        preloadImg.src = newSrc;

      } catch (error) {
        console.error('❌ Error in preloadAndUpdateImage:', error);
        reject(error);
      }
    });
  }

  updateTestimonialAfterImageFromProduct() {
    try {
      if (!this.productFirstImageUrl) {
        return;
      }
      const testimonialAfterImage = document.querySelector('.testimonial-image.after img');
      if (testimonialAfterImage) {
        testimonialAfterImage.src = this.productFirstImageUrl;
        testimonialAfterImage.alt = window.themeStrings.previewer.productImageAlt;
      }
    } catch (error) {
      console.error('❌ Error updating testimonial after image from product:', error);
    }
  }

  updateBackCoverImage(processedImageUrl) {
    try {
      // Find the back cover scene (last scene)
      const backSceneElement = document.querySelector(`[data-scene-index="${this.storyData.length - 1}"]`);

      if (backSceneElement) {
        const backSceneImage = backSceneElement.querySelector('.scene-image img');
        if (backSceneImage) {
          // Use preloading with existing loader methods
          const backSceneOrder = this.storyData.length - 1;
          this.preloadAndUpdateSceneImage(backSceneImage, processedImageUrl, window.themeStrings.previewer.processedBackCoverAlt, backSceneOrder);
        }
      } else {
        console.warn('⚠️ Back cover scene element not found');
      }
    } catch (error) {
      console.error('❌ Error updating back cover image:', error);
    }
  }

  async handlePhotoUpload(event) {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (
        !['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'].includes(file.type)
        && !/\.(?:jpe?g|png|webp|heic|heif)$/i.test(file.name || '')
      ) {
        this.showToaster(window.themeStrings.previewer.selectInvalidFile, 'error');
        return;
      }

      // Validate file size (max 20MB)
      if (file.size > 20 * 1024 * 1024) {
        this.showToaster(window.themeStrings.previewer.imageTooLarge, 'error');
        return;
      }

      // Show loading state
      this.showLoadingState(window.themeStrings.previewer.analyzingPhoto);

      try {
        // Create image element for face validation
        const img = await this.createImageElement(file);

        // Check if Face API is loaded, if not wait for it
        if (!this.faceApiLoaded) {
          this.showLoadingState(window.themeStrings.previewer.loadingFaceModels);
          await this.initializeFaceApi();
        } else {

        }

        // Validate face using face-api.js with more lenient settings
        const faceValidation = await this.validateFace(img);

        if (faceValidation.success) {
          let finalFile = file;

          // If face was detected with bounding box, crop the image around the face
          if (faceValidation.faceBox) {
            this.showLoadingState(window.themeStrings.previewer.croppingImage);
            try {
              const croppedBlob = await this.cropImageAroundFace(img, faceValidation.faceBox, this.faceCropMarginFactor);
              // Convert blob to file
              finalFile = new File([croppedBlob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              console.log('✅ Image successfully cropped around face');
            } catch (cropError) {
              console.warn('⚠️ Face cropping failed, using original image:', cropError);
              // Continue with original file if cropping fails
            }
          }

          this.formData.photo = finalFile;
          this.updateUploadButton();
          this.validateForm();

          // Show preview with validation success
          this.showPhotoPreview(finalFile, faceValidation);
          this.showToaster(window.themeStrings.previewer.photoUploadedToast, 'success');
        }
      } catch (error) {
        console.error('Face validation error:', error);
        this.showToaster(error.message, 'error');
      } finally {
        this.hideLoadingState();
      }
    }
  }

  createImageElement(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(window.themeStrings.previewer.imageLoadFailed));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error(window.themeStrings.previewer.fileReadFailed));
      reader.readAsDataURL(file);
    });
  }

  showLoadingState(message) {
    const uploadSection = document.querySelector('.upload-section');
    if (uploadSection) {
      uploadSection.style.opacity = '0.6';
      uploadSection.style.pointerEvents = 'none';

      // Ensure spinner CSS exists (fallback if stylesheet not loaded)
      try {
        if (!document.getElementById('face-validation-spinner-styles')) {
          const style = document.createElement('style');
          style.id = 'face-validation-spinner-styles';
          style.textContent = `
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            .face-validation-loading { position:absolute; inset:0; background:rgba(255,255,255,0.95); display:flex; flex-direction:column; align-items:center; justify-content:center; z-index:10; border-radius:12px; }
            .face-validation-loading .loading-spinner { width:40px; height:40px; border:3px solid #f3f4f6; border-top:3px solid #8b5cf6; border-radius:50%; animation:spin 1s linear infinite; margin-bottom:1rem; }
            .face-validation-loading p { color:#8b5cf6; font-size:0.875rem; font-weight:600; margin:0; text-align:center; }
          `;
          document.head.appendChild(style);
        }
      } catch { }

      // Add or reuse loading overlay
      let loadingOverlay = uploadSection.querySelector('.face-validation-loading');
      if (!loadingOverlay) {
        loadingOverlay = document.createElement('div');
        loadingOverlay.className = 'face-validation-loading';
        loadingOverlay.innerHTML = `
          <div class="loading-spinner"></div>
          <p></p>
        `;
        uploadSection.appendChild(loadingOverlay);
      }
      const msg = loadingOverlay.querySelector('p');
      if (msg) msg.textContent = message;
    }
  }

  hideLoadingState() {
    const uploadSection = document.querySelector('.upload-section');
    const loadingOverlay = document.querySelector('.face-validation-loading');

    if (uploadSection) {
      uploadSection.style.opacity = '1';
      uploadSection.style.pointerEvents = 'auto';
    }

    if (loadingOverlay) {
      loadingOverlay.remove();
    }
  }

  showPhotoPreview(file, faceValidation = null) {
    const reader = new FileReader();
    reader.onload = (e) => {
      // Hide the upload section and show full preview
      const uploadSection = document.querySelector('.upload-section');
      const kidInfoSection = document.querySelector('.kid-info-section');

      // Create or update preview element
      let preview = document.querySelector('.photo-preview');
      if (!preview) {
        preview = document.createElement('div');
        preview.className = 'photo-preview full-preview';
        kidInfoSection.insertBefore(preview, uploadSection.nextSibling);
      }

      // Hide upload section
      uploadSection.style.display = 'none';

      const validationInfo = faceValidation ?
        `<div class="face-validation-success">
          <i class="fas fa-check-circle"></i> 
          ${window.themeStrings.previewer.faceDetected}
        </div>` : '';

      preview.innerHTML = `
        <div class="preview-image-container">
          <div class="preview-image-full">
            <img src="${e.target.result}" alt="${window.themeStrings.previewer.photoPreviewAlt}" />
            <button type="button" class="remove-photo" onclick="previewerController.removePhoto()">
              <i class="fas fa-times"></i>
              ${window.themeStrings.previewer.changePhoto}
            </button>
          </div>
          ${validationInfo}
          <p class="preview-text">${window.themeStrings.previewer.photoUploadedText}</p>
        </div>
      `;

      // Ensure form validation is triggered after preview is shown
      setTimeout(() => {
        this.validateForm();
      }, 50);
    };
    reader.readAsDataURL(file);
  }

  removePhoto() {
    this.formData.photo = null;
    const preview = document.querySelector('.photo-preview');
    const uploadSection = document.querySelector('.upload-section');

    if (preview) {
      preview.remove();
    }

    // Show upload section again
    if (uploadSection) {
      uploadSection.style.display = 'block';
    }

    const photoUpload = document.getElementById('photo-upload');
    if (photoUpload) {
      photoUpload.value = '';
    }

    this.updateUploadButton();
    this.validateForm();
  }

  updateUploadButton() {
    const uploadBtn = document.querySelector('.upload-btn');
    if (uploadBtn) {
      if (this.formData.photo) {
        uploadBtn.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          ${window.themeStrings.previewer.photoUploadedButton}
        `;
        uploadBtn.style.background = '#10b981';
      } else {
        uploadBtn.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          ${window.themeStrings.previewer.chooseImageButton}
        `;
        uploadBtn.style.background = '#8b5cf6';
      }
    }
  }

  handleNameChange(event) {
    const input = event.target;
    let value = input.value;

    // Check if Arabic validation is required (based on URL handle)
    if (this.isArabicLanguage()) {
      // Filter out anything that's not Arabic characters or spaces
      const arabicOnlyRegex = /[^\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\s]/g;
      value = value.replace(arabicOnlyRegex, '');
      input.value = value;

      // Validate Arabic characters and spaces only
      const arabicRegex = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\s]+$/;

      if (value && !arabicRegex.test(value)) {
      } else {
        // Hide hint only if input is completely valid or empty
        this.hideArabicHint();
      }
    } else {
      // Latin validation - filter out anything that's not Latin characters or spaces
      // Includes extended Latin for European languages (French, German, Spanish, etc.)
      const latinOnlyRegex = /[^a-zA-ZÀ-ÿ\u0100-\u017F\u0180-\u024F\u1E00-\u1EFF\s]/g;
      value = value.replace(latinOnlyRegex, '');
      input.value = value;

      // Validate Latin characters and spaces only
      const latinRegex = /^[a-zA-ZÀ-ÿ\u0100-\u017F\u0180-\u024F\u1E00-\u1EFF\s]+$/;

      if (value && !latinRegex.test(value)) {
      } else {
        // Hide hint only if input is completely valid or empty
        this.hideEnglishHint();
      }
    }

    // Limit to 15 characters
    if (value.length > 15) {
      value = value.substring(0, 15);
      input.value = value;
    }

    this.formData.childName = value;
    this.validateForm();
  }

  validateForm() {
    let isValid = this.formData.photo &&
      this.formData.childName.length >= 2;

    // Additional validation for Arabic language (based on URL handle)
    if (this.isArabicLanguage()) {
      isValid = isValid && this.isValidArabicText(this.formData.childName);
    } else {
      // Additional validation for English/Latin languages
      isValid = isValid && this.isValidEnglishText(this.formData.childName);
    }

    const previewBtn = document.querySelector('.preview-btn');

    if (previewBtn) {
      previewBtn.disabled = !isValid;

      if (isValid) {
        // Show normal button state when form is valid
        previewBtn.innerHTML = `
          <span class="button-text">${window.themeStrings.previewer.previewStoryButton} <i class="fas fa-arrow-right"></i></span>
        `;
        previewBtn.classList.remove('loading');
      } else {
        // Show disabled state without spinner (just disabled button)
        previewBtn.innerHTML = `
          <span class="button-text">${window.themeStrings.previewer.previewStoryButton} <i class="fas fa-arrow-right"></i></span>
        `;
        previewBtn.classList.remove('loading');
      }
    }

    return isValid;
  }

  async handlePreviewClick() {
    if (this.validateForm()) {
      // Generate random ID for this preview session
      this.generationId = this.generateRandomId();

      // Save generation ID to local storage
      this.saveGenerationIdToLocalStorage(this.generationId);

      // Get the preview button and show loading state
      const previewBtn = document.querySelector('.preview-btn');
      if (previewBtn) {
        // Store original button content
        const originalContent = previewBtn.innerHTML;

        // Show loading state on button with spinner
        previewBtn.innerHTML = `
          <div class="spinner"></div>
          <span class="button-text">${window.themeStrings.previewer.processingPreviewButton}</span>
        `;
        previewBtn.disabled = true;
        previewBtn.classList.add('loading');

        try {
          // Show loading toaster
          this.showToaster(window.themeStrings.previewer.processingPreviewToast, 'info');

          // Send preview request to API
          const previewResult = await this.sendPreviewRequest();

          if (previewResult) {
            this.showToaster(window.themeStrings.previewer.previewSent, 'success');
            this.goToStep(3);
          } else {
            this.showToaster(window.themeStrings.previewer.previewFailed, 'error');
            // Restore button state on error
            previewBtn.innerHTML = originalContent;
            previewBtn.disabled = false;
            previewBtn.classList.remove('loading');
            return;
          }
        } catch (error) {
          console.error('❌ Error in preview process:', error);
          this.showToaster(window.themeStrings.previewer.previewError, 'error');
          // Restore button state on error
          previewBtn.innerHTML = originalContent;
          previewBtn.disabled = false;
          previewBtn.classList.remove('loading');
          return;
        }
      }
    }
  }

  goToStep(step) {
    if (!this.canNavigateToStep(step)) return;

    // Hide current step
    const currentStepContent = document.getElementById(`step-${this.currentStep}`);
    if (currentStepContent) {
      currentStepContent.classList.remove('active');
      currentStepContent.style.display = 'none';
    }

    // Show new step
    const newStepContent = document.getElementById(`step-${step}`);
    if (newStepContent) {
      newStepContent.style.display = 'block';
      // Small delay for animation
      setTimeout(() => {
        newStepContent.classList.add('active');
      }, 50);
    }

    this.currentStep = step;
    this.updateStepNavigation();

    // Update content based on step
    if (step === 3) {
      this.updatePreviewContent();

      // Initialize Pusher when entering step 3
      this.initializePusherIfNeeded().catch(err => console.warn('Pusher initialization failed:', err));

      // Scroll to top of previewer-content after transitioning to preview step
      setTimeout(() => {
        const previewerContent = document.querySelector('.previewer-content');
        if (previewerContent) {
          previewerContent.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  }

  canNavigateToStep(step) {
    // Prevent navigation to step 1 (Story page)
    if (step === 1) return false;

    // Can always go back
    if (step < this.currentStep) return true;

    // Can only move forward if current step is complete
    switch (this.currentStep) {
      case 1:
        return true; // Story page is already completed
      case 2:
        return this.validateForm(); // Kid info must be complete (photo + name)
      case 3:
        return true; // Preview is always accessible once kid info is complete
      default:
        return false;
    }
  }

  updateStepNavigation() {
    const stepItems = document.querySelectorAll('.step-item');

    stepItems.forEach((item, index) => {
      const stepNumber = index + 1;
      const stepIcon = item.querySelector('.step-icon');

      item.classList.remove('active', 'completed');

      if (stepNumber < this.currentStep || (stepNumber === 1)) {
        item.classList.add('completed');
        // Update icon to Font Awesome check
        stepIcon.innerHTML = '<i class="fas fa-check"></i>';
      } else if (stepNumber === this.currentStep) {
        item.classList.add('active');
        // Show step number for active step
        stepIcon.innerHTML = stepNumber;
      } else {
        // Show step number for pending steps
        stepIcon.innerHTML = stepNumber;
      }
    });

    // Update step connector class based on current step
    const stepConnector = document.querySelector('.step-connector');
    if (stepConnector) {
      stepConnector.className = `step-connector step-${this.currentStep}-completed`;
    }
  }

  updatePreviewContent() {
    // Update header text with child name and story title inline
    const storyHeaderText = document.getElementById('storyHeaderText');
    if (storyHeaderText) {
      let headerText = window.themeStrings.previewer.previewTitle;

      if (this.formData.childName) {
        headerText = themeFmt(window.themeStrings.previewer.previewTitleNamed, { name: this.formData.childName });
      }

      storyHeaderText.textContent = headerText;
    }

    // Update additional story CTA
    this.updateAdditionalStoryCTA();
  }

  async updateAdditionalStoryCTA() {
    try {
      // Update kid name in the text
      const kidNamePlaceholder = document.getElementById('kidNamePlaceholder');
      if (kidNamePlaceholder && this.formData.childName) {
        kidNamePlaceholder.textContent = this.formData.childName;
      }

      // Get current cart state to calculate discount
      const cartState = await this.getStandardCartState();
      const itemCount = cartState ? cartState.itemCount : 0;

      // Calculate discount percentage based on cart quantity
      let discountPercentage;

      if (itemCount === 0) {
        discountPercentage = 15; // 15% for 0 items
      } else if (itemCount === 1) {
        discountPercentage = 20; // 20% for 1 item
      } else {
        discountPercentage = 25; // 25% for 2+ items
      }

      // Update discount percentage in button
      const discountSpan = document.getElementById('discountPercentage');
      if (discountSpan) {
        discountSpan.textContent = `${discountPercentage}%`;
      }

      // If we have a generation ID, update the story name in the CTA
      if (this.generationId) {
        const additionalStoryText = document.getElementById('additionalStoryText');
        if (additionalStoryText && this.formData.childName) {
          // Update the text to reflect that this is a completed generation
          additionalStoryText.innerHTML = themeFmt(window.themeStrings.previewer.additionalStoryText, {
            name: `<span id="kidNamePlaceholder">${this.formData.childName}</span>`
          });
        }
      }

    } catch (error) {
      console.error('❌ Error updating additional story CTA:', error);
    }
  }

  async getStandardCartState() {
    try {
      const response = await fetch(localeUrl('cart.js'));
      if (response.ok) {
        const cart = await response.json();
        return {
          itemCount: cart.item_count,
          totalPrice: cart.total_price,
          items: cart.items.map(item => ({ id: item.variant_id, quantity: item.quantity }))
        };
      }
    } catch (error) {
      console.error('❌ Error getting cart state:', error);
    }
    return null;
  }

  async handleCheckout() {
    try {
      // If we have a generation ID, we already have the data
      if (this.generationId) {
        // Show loading state
        this.showToaster(window.themeStrings.previewer.processingOrder, 'info');

        // Add to cart with discount
        const cartResult = await this.addToStandardShopifyCart();

        if (cartResult) {
          this.showToaster(window.themeStrings.previewer.checkoutRedirect, 'success');

          setTimeout(() => {
            window.location.href = localeUrl('checkout');
          }, 1500);
        } else {
          this.showToaster(window.themeStrings.previewer.addToCartFailed, 'error');
          return;
        }
      } else {
        // Original validation for new generations
        if (!this.formData.childName || !this.formData.photo) {
          this.showToaster(window.themeStrings.previewer.missingInfo, 'error');
          return;
        }

        // Show loading state
        this.showToaster(window.themeStrings.previewer.processingOrder, 'info');

        // Use the stored uploaded image URL instead of uploading again
        const kidImageUrl = this.uploadedImageUrl;

        if (!kidImageUrl) {
          this.showToaster(window.themeStrings.previewer.imageUnavailable, 'error');
          return;
        }

        // Add to cart with discount
        const cartResult = await this.addToStandardShopifyCart();

        if (cartResult) {
          this.showToaster(window.themeStrings.previewer.checkoutRedirect, 'success');

          setTimeout(() => {
            window.location.href = localeUrl('checkout');
          }, 1500);
        } else {
          this.showToaster(window.themeStrings.previewer.addToCartFailed, 'error');
          return;
        }
      }

    } catch (error) {
      console.error('❌ Checkout process failed:', error);
      this.showToaster(window.themeStrings.previewer.checkoutError, 'error');
    }
  }

  handleShare() {
    try {
      let shareUrl;

      // Always use the API base URL with generation ID
      if (this.generationId) {
        const isArabic = document.documentElement.lang === 'ar' || document.documentElement.lang === 'ar-EG';
        const apiBaseUrl = 'https://api.bysnaps.ai';
        const languagePath = isArabic ? '/ar' : '';
        shareUrl = `https://preview.bysnaps.ai${languagePath}/pages/preview?generation_id=${this.generationId}`;
      } else {
        // Fallback to current page URL if no generation ID
        shareUrl = window.location.href;
      }

      // Check if Web Share API is available (mobile devices)
      if (navigator.share) {
        navigator.share({
          title: document.title,
          url: shareUrl
        }).catch((error) => {

          this.fallbackShare(shareUrl);
        });
      } else {
        // Fallback for desktop browsers
        this.fallbackShare(shareUrl);
      }
    } catch (error) {
      console.error('❌ Share process failed:', error);
      this.showToaster(window.themeStrings.previewer.shareFailed, 'error');
    }
  }

  fallbackShare(url) {
    try {
      // Create a temporary input element
      const tempInput = document.createElement('input');
      tempInput.value = url;
      document.body.appendChild(tempInput);

      // Select and copy the URL
      tempInput.select();
      tempInput.setSelectionRange(0, 99999); // For mobile devices
      document.execCommand('copy');

      // Remove the temporary element
      document.body.removeChild(tempInput);

      // Show success message
      this.showToaster(window.themeStrings.previewer.linkCopied, 'success');
    } catch (error) {
      console.error('❌ Fallback share failed:', error);
      // Last resort: open in new window
      window.open(url, '_blank');
    }
  }

  async addToStandardShopifyCart() {
    try {
      // If we have a generation ID, we need to get the variant ID from the story slug
      let variantId = this.productVariantId;

      if (this.generationId && !variantId) {
        // Try to get variant ID from the story slug in the URL
        const storySlugParam = this.getUrlParameter('story');
        const normalizedStorySlug = this.normalizeProductHandle(storySlugParam);
        if (normalizedStorySlug) {
          // Fetch product data to get variant ID
          await this.fetchProductData(normalizedStorySlug);
          variantId = this.productVariantId;
        }

        // If still no variant ID, try to get it from the generation data
        if (!variantId && this.storyData) {
          variantId = await this.getVariantIdFromGeneration();
        }
      }

      if (!variantId) {
        console.error('❌ No variant ID available. Product data may not be loaded.');
        this.showToaster(window.themeStrings.previewer.productInfoUnavailable, 'error');
        return false;
      }

      // Check if this generation is already in the cart
      if (this.generationId) {
        const isAlreadyInCart = await this.checkIfGenerationInCart();
        if (isAlreadyInCart) {
          return true; // Return true to continue with the button function
        }
      }

      // Prepare form data for standard Shopify cart
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      // Add kid details as line item properties
      formData.append('properties[Kid Name]', this.formData.childName);
      formData.append('properties[Generation ID]', this.generationId || 'N/A');
      if (this.uploadedImageUrl) {
        formData.append('properties[Photo 1]', this.normalizeApiUrl(this.uploadedImageUrl));
      }

      // Add to standard Shopify cart via AJAX API
      const response = await fetch(localeUrl('cart/add.js'), {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return true;
      } else {
        const errorText = await response.text();
        console.error('❌ Standard cart add failed:', response.status, errorText);
        return false;
      }

    } catch (error) {
      console.error('❌ Error adding to standard Shopify cart:', error);
      return false;
    }
  }

  async handleAddAnotherStory() {
    try {
      // If we have a generation ID, we already have the data
      if (this.generationId) {
        // Show loading state
        this.showToaster(window.themeStrings.previewer.addingStory, 'info');

        // Add to cart with discount
        const cartResult = await this.addToStandardShopifyCart();

        if (cartResult) {
          this.showToaster(window.themeStrings.previewer.storyAdded, 'success');

          setTimeout(() => {
            window.location.href = localeUrl('collections/stories');
          }, 1500);
        } else {
          this.showToaster(window.themeStrings.previewer.addToCartFailed, 'error');
          return;
        }
      } else {
        // Original validation for new generations
        if (!this.formData.childName || !this.formData.photo) {
          this.showToaster(window.themeStrings.previewer.missingInfo, 'error');
          return;
        }

        // Show loading state
        this.showToaster(window.themeStrings.previewer.addingStory, 'info');

        // Use the stored uploaded image URL instead of uploading again
        const kidImageUrl = this.uploadedImageUrl;

        if (!kidImageUrl) {
          this.showToaster(window.themeStrings.previewer.imageUnavailable, 'error');
          return;
        }

        // Add to cart with discount
        const cartResult = await this.addToStandardShopifyCart();

        if (cartResult) {
          this.showToaster(window.themeStrings.previewer.storyAdded, 'success');

          setTimeout(() => {
            window.location.href = localeUrl('collections/stories');
          }, 1500);
        } else {
          this.showToaster(window.themeStrings.previewer.addToCartFailed, 'error');
          return;
        }
      }

    } catch (error) {
      console.error('❌ Add another story process failed:', error);
      this.showToaster(window.themeStrings.previewer.addStoryError, 'error');
    }
  }

  showToaster(message, type = 'info') {
    // Remove any existing toasters
    const existingToaster = document.querySelector('.toaster');
    if (existingToaster) {
      existingToaster.remove();
    }

    // Create toaster element
    const toaster = document.createElement('div');
    toaster.className = `toaster toaster-${type}`;

    // Set icon based on type
    let icon = '';
    switch (type) {
      case 'success':
        icon = '<i class="fas fa-check-circle"></i>';
        break;
      case 'error':
        icon = '<i class="fas fa-exclamation-circle"></i>';
        break;
      case 'warning':
        icon = '<i class="fas fa-exclamation-triangle"></i>';
        break;
      default:
        icon = '<i class="fas fa-info-circle"></i>';
    }

    toaster.innerHTML = `
      <div class="toaster-content">
        ${icon}
        <span class="toaster-message">${message}</span>
        <button class="toaster-close" onclick="this.parentElement.parentElement.remove()">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `;

    // Add toaster to document
    document.body.appendChild(toaster);

    // Animate in
    setTimeout(() => {
      toaster.classList.add('toaster-show');
    }, 100);

    // Auto remove after 5 seconds for errors, 3 seconds for success
    const timeout = type === 'error' ? 5000 : 3000;
    setTimeout(() => {
      if (toaster && toaster.parentElement) {
        toaster.classList.remove('toaster-show');
        setTimeout(() => {
          if (toaster && toaster.parentElement) {
            toaster.remove();
          }
        }, 300);
      }
    }, timeout);
  }

  showErrorOverlay() {
    const errorOverlay = document.querySelector('.page-overlay-error');
    if (errorOverlay) {
      errorOverlay.classList.remove('hidden');

      // Start countdown
      let countdown = 5;
      const countdownElement = document.getElementById('errorCountdown');

      const countdownInterval = setInterval(() => {
        countdown--;
        if (countdownElement) {
          countdownElement.textContent = countdown;
        }

        if (countdown <= 0) {
          clearInterval(countdownInterval);
          window.location.href = localeUrl('');
        }
      }, 1000);
    }
  }

  hideErrorOverlay() {
    const errorOverlay = document.querySelector('.page-overlay-error');
    if (errorOverlay) {
      errorOverlay.classList.add('hidden');
    }
  }

  hidePageLoader() {
    const pageLoader = document.querySelector('.page-overlay-loader');
    if (pageLoader) {
      pageLoader.classList.add('hidden');
    }
  }

  generateRandomId() {
    const timestamp = Date.now();
    const randomChars = Math.random().toString(36).substring(2, 8); // 6 random characters
    const generationId = `PRVSHP${timestamp}-${randomChars}`;
    return generationId;
  }

  checkAndAddGenerationToLocalStorage(generationId) {
    try {
      // Get existing generations array from local storage
      const existingGenerations = this.getGenerationsFromLocalStorage();

      // Check if this generation ID already exists
      const existingGeneration = existingGenerations.find(gen => gen.id === generationId);

      if (!existingGeneration) {
        // Generation doesn't exist, add it with basic info (will be updated later with full data)
        const newGeneration = {
          id: generationId,
          timestamp: new Date().toISOString(),
          childName: 'Loading...', // Will be updated when data is fetched
          storySlug: this.normalizeProductHandle(this.getUrlParameter('story')) || 'unknown',
          storyTitle: 'Loading Story...' // Will be updated when data is fetched
        };

        // Add to the beginning of the array (most recent first)
        existingGenerations.unshift(newGeneration);

        // Limit to 100 generations to prevent storage bloat
        if (existingGenerations.length > 100) {
          existingGenerations.splice(100);
        }

        // Save back to local storage
        localStorage.setItem('snaps_generations', JSON.stringify(existingGenerations));
      }

    } catch (error) {
      console.error('❌ Error checking and adding generation to local storage:', error);
    }
  }

  updateGenerationInLocalStorage(generationId, generatedData) {
    try {
      // Get existing generations array from local storage
      const existingGenerations = this.getGenerationsFromLocalStorage();

      // Find the generation to update
      const generationIndex = existingGenerations.findIndex(gen => gen.id === generationId);

      if (generationIndex !== -1) {
        // Update existing generation with complete data
        existingGenerations[generationIndex] = {
          id: generationId,
          timestamp: existingGenerations[generationIndex].timestamp, // Keep original timestamp
          childName: generatedData.kid_name || existingGenerations[generationIndex].childName,
          storySlug: this.normalizeProductHandle(generatedData.story_slug || this.getUrlParameter('story') || existingGenerations[generationIndex].storySlug),
          storyTitle: generatedData.story_name || existingGenerations[generationIndex].storyTitle
        };

        // Save back to local storage
        localStorage.setItem('snaps_generations', JSON.stringify(existingGenerations));
      }

    } catch (error) {
      console.error('❌ Error updating generation in local storage:', error);
    }
  }

  saveGenerationIdToLocalStorage(generationId) {
    try {
      // Get existing generations array from local storage
      const existingGenerations = this.getGenerationsFromLocalStorage();

      // Add new generation ID with timestamp and metadata
      const newGeneration = {
        id: generationId,
        timestamp: new Date().toISOString(),
        childName: this.formData.childName,
        storySlug: this.storySlug,
        storyTitle: this.productMetafields?.story_title || 'Unknown Story'
      };

      // Add to the beginning of the array (most recent first)
      existingGenerations.unshift(newGeneration);

      // Limit to 100 generations to prevent storage bloat
      if (existingGenerations.length > 100) {
        existingGenerations.splice(100);
      }

      // Save back to local storage
      localStorage.setItem('snaps_generations', JSON.stringify(existingGenerations));

    } catch (error) {
      console.error('❌ Error saving generation ID to local storage:', error);
    }
  }

  getGenerationsFromLocalStorage() {
    try {
      const stored = localStorage.getItem('snaps_generations');
      if (stored) {
        return JSON.parse(stored);
      }
      return [];
    } catch (error) {
      console.error('❌ Error reading generations from local storage:', error);
      return [];
    }
  }

  getGenerationById(generationId) {
    try {
      const generations = this.getGenerationsFromLocalStorage();
      return generations.find(gen => gen.id === generationId);
    } catch (error) {
      console.error('❌ Error getting generation by ID:', error);
      return null;
    }
  }

  getAllGenerations() {
    return this.getGenerationsFromLocalStorage();
  }

  clearAllGenerations() {
    try {
      localStorage.removeItem('snaps_generations');
    } catch (error) {
      console.error('❌ Error clearing generations from local storage:', error);
    }
  }

  async sendPreviewRequest() {
    try {
      const requestBody = new FormData();
      requestBody.append('slug', this.storySlug);
      requestBody.append('kid_name', this.formData.childName);
      requestBody.append('generation_id', this.generationId);
      requestBody.append('kid_image', this.formData.photo, this.formData.photo.name);

      const response = await fetch('https://api.bysnaps.ai/previewer-stories/Shopify/process', {
        method: 'POST',
        headers: {
          'Accept': 'application/json'
        },
        body: requestBody
      });

      if (response.ok) {
        const data = await response.json();
        this.uploadedImageUrl = this.normalizeApiUrl(data.kid_image);

        // Trigger Pusher event to notify about preview request
        this.triggerPreviewRequestEvent();

        // Start countdown timers for all scene loaders now that API responded
        this.startAllSceneLoaderCountdowns(55);

        // Show WhatsApp popup after 5 seconds
        setTimeout(() => {
          this.showWhatsAppPopup();
        }, 5000);

        return true;
      } else {
        const errorText = await response.text();
        console.error('❌ Preview request failed:', response.status, errorText);
        return false;
      }

    } catch (error) {
      console.error('❌ Error sending preview request:', error);
      return false;
    }
  }

  triggerPreviewRequestEvent() {
    try {
      // You can trigger a client event to notify other parts of your app
      const channel = this.pusher.channel('Previewer');
      if (channel) {
        channel.trigger('client-preview-requested', {
          generation_id: this.generationId,
          kid_name: this.formData.childName,
          story_slug: this.storySlug,
          timestamp: new Date().toISOString()
        });
      }

    } catch (error) {
      console.error('❌ Error triggering Pusher event:', error);
    }
  }

  showWhatsAppPopup() {
    try {
      if (typeof window.showWhatsAppTooltip !== 'undefined' && !window.showWhatsAppTooltip) {
        return;
      }

      // Find the existing WhatsApp floating button
      const whatsappButton = document.querySelector('.whatsapp-float');

      if (!whatsappButton) {
        console.error('❌ WhatsApp floating button not found');
        return;
      }

      // Create tooltip popup
      const tooltipPopup = document.createElement('div');
      tooltipPopup.className = 'whatsapp-tooltip-popup';
      tooltipPopup.innerHTML = `
        <div class="whatsapp-tooltip-content">
          <div class="whatsapp-tooltip-header">
            <div class="whatsapp-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/>
              </svg>
            </div>
            <button class="whatsapp-tooltip-close" onclick="previewerController.closeWhatsAppPopup()">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <div class="whatsapp-tooltip-body">
            <h4>${window.themeStrings.previewer.whatsappTitle}</h4>
            <p>${window.themeStrings.previewer.whatsappBody}</p>
            <button class="whatsapp-tooltip-cta" onclick="window.open('https://wa.me/201039402869?text=I need updates about ${this.productMetafields?.story_title || 'Story'} Story for ${this.formData.childName} with ID ${this.generationId}', '_blank')">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/>
              </svg>
              ${window.themeStrings.previewer.whatsappCta}
            </button>
          </div>
        </div>
      `;

      // Position tooltip near the WhatsApp button and append to body to avoid overlay issues
      const positionTooltip = () => {
        const btnRect = whatsappButton.getBoundingClientRect();
        tooltipPopup.style.position = 'fixed';
        // Place tooltip above the button (approx 70px) and aligned to the button's right offset
        const rightOffset = Math.max(10, window.innerWidth - btnRect.right);
        const bottomOffset = Math.max(10, window.innerHeight - btnRect.bottom + 70);
        tooltipPopup.style.right = `${rightOffset}px`;
        tooltipPopup.style.bottom = `${bottomOffset}px`;
        tooltipPopup.style.zIndex = '10001';
      };
      document.body.appendChild(tooltipPopup);
      positionTooltip();
      // Reposition on resize
      this.whatsappTooltipPositionHandler = positionTooltip;
      window.addEventListener('resize', this.whatsappTooltipPositionHandler);

      // Update the WhatsApp button to include the message link
      const whatsappUrl = `https://wa.me/201039402869?text=I need updates about ${this.productMetafields?.story_title || 'Story'} for ${this.formData.childName} with ID ${this.generationId}`;
      whatsappButton.href = whatsappUrl;
      whatsappButton.target = '_blank';

      // Add click handler to make entire button clickable
      whatsappButton.addEventListener('click', (e) => {
        // Don't prevent default - let the link work
      });

      // Animate in
      setTimeout(() => {
        tooltipPopup.classList.add('show');
      }, 100);

    } catch (error) {
      console.error('❌ Error showing WhatsApp tooltip:', error);
    }
  }

  closeWhatsAppPopup() {
    try {
      const tooltipPopup = document.querySelector('.whatsapp-tooltip-popup');
      if (tooltipPopup) {
        tooltipPopup.classList.remove('show');
        setTimeout(() => {
          if (tooltipPopup && tooltipPopup.parentElement) {
            tooltipPopup.remove();
          }
        }, 300);
      }
      // Clean up resize listener if set
      if (this.whatsappTooltipPositionHandler) {
        window.removeEventListener('resize', this.whatsappTooltipPositionHandler);
        this.whatsappTooltipPositionHandler = null;
      }
    } catch (error) {
      console.error('❌ Error closing WhatsApp tooltip:', error);
    }
  }

  setupArabicValidation() {
    try {
      const nameInput = document.querySelector('input[name="child-name"]');
      if (nameInput) {
        // Add placeholder based on current theme language
        const placeholder = this.getChildNamePlaceholder();
        nameInput.setAttribute('placeholder', placeholder);

        // Add data attribute for Arabic validation
        nameInput.setAttribute('data-arabic-validation', 'true');

        // Add input event listener for real-time validation and filtering
        nameInput.addEventListener('input', (e) => {
          this.handleNameChange(e);
        });

        // Add keydown event to prevent invalid characters from being typed
        nameInput.addEventListener('keydown', (e) => {
          this.filterInvalidCharacters(e);
        });

        // Add keyup event to continuously check for invalid characters
        nameInput.addEventListener('keyup', (e) => {
          this.checkArabicValidation(e.target);
        });

        // Add paste event to check pasted content
        nameInput.addEventListener('paste', (e) => {
          setTimeout(() => {
            this.checkArabicValidation(e.target);
          }, 10);
        });

        // Add focus event to show hint on focus if needed
        nameInput.addEventListener('focus', (e) => {
        });

        // Keep hint visible on blur - don't hide it
        nameInput.addEventListener('blur', (e) => {
          // Only hide hint if the input is completely valid or empty
          if (!e.target.value || this.isValidArabicText(e.target.value)) {
            this.hideArabicHint();
          }
        });
      }
    } catch (error) {
      console.error('❌ Error setting up Arabic validation:', error);
    }
  }

  setupEnglishValidation() {
    try {
      const nameInput = document.querySelector('input[name="child-name"]');
      if (nameInput) {
        // Add placeholder based on current theme language
        const placeholder = this.getChildNamePlaceholder();
        nameInput.setAttribute('placeholder', placeholder);

        // Add data attribute for English validation
        nameInput.setAttribute('data-english-validation', 'true');

        // Add input event listener for real-time validation and filtering
        nameInput.addEventListener('input', (e) => {
          this.handleNameChange(e);
        });

        // Add keydown event to prevent invalid characters from being typed
        nameInput.addEventListener('keydown', (e) => {
          this.filterEnglishCharacters(e);
        });

        // Add keyup event to continuously check for invalid characters
        nameInput.addEventListener('keyup', (e) => {
          this.checkEnglishValidation(e.target);
        });

        // Add paste event to check pasted content
        nameInput.addEventListener('paste', (e) => {
          setTimeout(() => {
            this.checkEnglishValidation(e.target);
          }, 10);
        });

        // Add focus event to show hint on focus if needed
        nameInput.addEventListener('focus', (e) => {
          const value = e.target.value;
          if (value) {
            // Check if there are Arabic characters or invalid characters
            const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
            const hasArabicChars = arabicRegex.test(value);
            const latinRegex = /^[a-zA-ZÀ-ÿ\u00C0-\u017F\u0180-\u024F\u1E00-\u1EFF\u2C60-\u2C7F\uA720-\uA7FF\s]+$/;

          }
        });

        // Keep hint visible on blur - don't hide it
        nameInput.addEventListener('blur', (e) => {
          const value = e.target.value;
          // Only hide hint if the input is completely valid (no Arabic chars) or empty
          if (!value) {
            this.hideEnglishHint();
          } else {
            // Check if there are Arabic characters or invalid characters
            const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
            const hasArabicChars = arabicRegex.test(value);
            const latinRegex = /^[a-zA-ZÀ-ÿ\u00C0-\u017F\u0180-\u024F\u1E00-\u1EFF\u2C60-\u2C7F\uA720-\uA7FF\s]+$/;

            if (!hasArabicChars && latinRegex.test(value)) {
              this.hideEnglishHint();
            }
          }
        });
      }
    } catch (error) {
      console.error('❌ Error setting up English validation:', error);
    }
  }

  isValidArabicText(text) {
    if (!text) return true;
    // Validate Arabic characters and spaces only (no punctuation marks like : ; , .)
    const arabicRegex = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\s]+$/;
    return arabicRegex.test(text);
  }

  isValidEnglishText(text) {
    if (!text) return true;
    // Validate Latin characters and spaces only (includes extended Latin for all European languages)
    const latinRegex = /^[a-zA-ZÀ-ÿ\u0100-\u017F\u0180-\u024F\u1E00-\u1EFF\s]+$/;
    return latinRegex.test(text);
  }

  showArabicHint(inputElement) {
    try {
      // Remove existing hint
      this.hideArabicHint();

      // Get translation from locale based on current theme language
      const translation = this.getArabicValidationMessage();

      // Create hint div element
      const hintDiv = document.createElement('div');
      hintDiv.className = 'arabic-validation-hint';
      hintDiv.innerHTML = `
        <div class="hint-content">
          <i class="fas fa-info-circle"></i>
          <span>${translation}</span>
        </div>
      `;

      // Remove existing hint if any
      const existingHint = document.querySelector('.arabic-validation-hint');
      if (existingHint) {
        existingHint.remove();
      }

      // Position hint div above the input field with fixed positioning
      const inputRect = inputElement.getBoundingClientRect();
      const hintHeight = 40; // Approximate hint height
      const spacing = 40; // Space between hint and input (increased by 10px)

      // Calculate position
      let topPosition = inputRect.top - hintHeight - spacing;
      let leftPosition = inputRect.left;

      // Ensure hint stays within viewport
      if (topPosition < 10) {
        topPosition = 10; // Minimum distance from top
      }

      if (leftPosition + inputRect.width > window.innerWidth) {
        leftPosition = window.innerWidth - inputRect.width - 10; // Keep within right edge
      }

      hintDiv.style.position = 'fixed';
      hintDiv.style.top = `${topPosition}px`;
      hintDiv.style.left = `${leftPosition}px`;
      hintDiv.style.width = `${inputRect.width}px`;
      hintDiv.style.zIndex = '1000';

      // Add hint to document body
      document.body.appendChild(hintDiv);

      // Store reference to input element for repositioning
      hintDiv.setAttribute('data-input-id', inputElement.id || 'child-name-input');

      // Add event listeners for repositioning
      this.addHintRepositionListeners(hintDiv, inputElement);

      // Add CSS styles
      if (!document.querySelector('#arabic-hint-styles')) {
        const styles = document.createElement('style');
        styles.id = 'arabic-hint-styles';
        styles.textContent = `
          .arabic-validation-hint {
            background: #fef3c7;
            border: 1px solid #f59e0b;
            border-radius: 6px;
            padding: 8px 12px;
            font-size: 13px;
            color: #92400e;
            animation: hintFadeIn 0.3s ease-in-out;
            box-sizing: border-box;
            position: fixed;
            z-index: 1000;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            max-width: none;
            white-space: normal;
          }
          
          .arabic-validation-hint .hint-content {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          .arabic-validation-hint i {
            color: #f59e0b;
            font-size: 14px;
            flex-shrink: 0;
          }
          
          .arabic-validation-hint span {
            flex: 1;
            line-height: 1.4;
            font-weight: 500;
            text-align: left;
          }
          
          @keyframes hintFadeIn {
            from {
              opacity: 0;
              transform: translateY(5px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `;
        document.head.appendChild(styles);
      }

    } catch (error) {
      console.error('❌ Error showing Arabic tooltip:', error);
    }
  }

  hideArabicHint() {
    try {
      const hintDiv = document.querySelector('.arabic-validation-hint');
      if (hintDiv) {
        // Clean up event listeners
        if (hintDiv._resizeHandler) {
          window.removeEventListener('resize', hintDiv._resizeHandler);
        }
        if (hintDiv._scrollHandler) {
          window.removeEventListener('scroll', hintDiv._scrollHandler, true);
        }

        hintDiv.remove();
      }
    } catch (error) {
      console.error('❌ Error hiding Arabic hint:', error);
    }
  }

  showEnglishHint(inputElement) {
    try {
      // Remove existing hint if any
      const existingHint = document.querySelector('.english-validation-hint');
      if (existingHint) {
        existingHint.remove();
      }

      // Get translation from locale based on current theme language
      const translation = this.getEnglishValidationMessage();

      // Create hint div element
      const hintDiv = document.createElement('div');
      hintDiv.className = 'english-validation-hint';
      hintDiv.innerHTML = `
        <div class="hint-content">
          <i class="fas fa-info-circle"></i>
          <span>${translation}</span>
        </div>
      `;

      // Position hint div above the input field with fixed positioning
      const inputRect = inputElement.getBoundingClientRect();
      const hintHeight = 40; // Approximate hint height
      const spacing = 40; // Space between hint and input

      // Calculate position
      let topPosition = inputRect.top - hintHeight - spacing;
      let leftPosition = inputRect.left;

      // Ensure hint stays within viewport
      if (topPosition < 10) {
        topPosition = 10; // Minimum distance from top
      }

      if (leftPosition + inputRect.width > window.innerWidth) {
        leftPosition = window.innerWidth - inputRect.width - 10; // Keep within right edge
      }

      hintDiv.style.position = 'fixed';
      hintDiv.style.top = `${topPosition}px`;
      hintDiv.style.left = `${leftPosition}px`;
      hintDiv.style.width = `${inputRect.width}px`;
      hintDiv.style.zIndex = '1000';

      // Add hint to document body
      document.body.appendChild(hintDiv);

      // Store reference to input element for repositioning
      hintDiv.setAttribute('data-input-id', inputElement.id || 'child-name-input');

      // Add event listeners for repositioning
      this.addHintRepositionListeners(hintDiv, inputElement);

      // Add CSS styles
      if (!document.querySelector('#english-hint-styles')) {
        const styles = document.createElement('style');
        styles.id = 'english-hint-styles';
        styles.textContent = `
          .english-validation-hint {
            background: #fef3c7;
            border: 1px solid #f59e0b;
            border-radius: 6px;
            padding: 8px 12px;
            font-size: 13px;
            color: #92400e;
            animation: hintFadeIn 0.3s ease-in-out;
            box-sizing: border-box;
            position: fixed;
            z-index: 1000;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            max-width: none;
            white-space: normal;
          }
          
          .english-validation-hint .hint-content {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          .english-validation-hint i {
            color: #f59e0b;
            font-size: 14px;
            flex-shrink: 0;
          }
          
          .english-validation-hint span {
            flex: 1;
            line-height: 1.4;
            font-weight: 500;
            text-align: left;
          }
          
          @keyframes hintFadeIn {
            from {
              opacity: 0;
              transform: translateY(5px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `;
        document.head.appendChild(styles);
      }

    } catch (error) {
      console.error('❌ Error showing English hint:', error);
    }
  }

  hideEnglishHint() {
    try {
      const hintDiv = document.querySelector('.english-validation-hint');
      if (hintDiv) {
        // Clean up event listeners
        if (hintDiv._resizeHandler) {
          window.removeEventListener('resize', hintDiv._resizeHandler);
        }
        if (hintDiv._scrollHandler) {
          window.removeEventListener('scroll', hintDiv._scrollHandler, true);
        }

        hintDiv.remove();
      }
    } catch (error) {
      console.error('❌ Error hiding English hint:', error);
    }
  }

  findFormContainer(element) {
    try {
      // Look for common form container selectors
      const selectors = [
        '.kid-info-section',
        '.form-group',
        '.input-group',
        '.field-group',
        'form',
        '.previewer-form'
      ];

      let container = element;
      while (container && container !== document.body) {
        for (const selector of selectors) {
          if (container.matches(selector)) {
            return container;
          }
        }
        container = container.parentElement;
      }

      // Fallback to the closest div with class
      return element.closest('div[class*="section"], div[class*="form"], div[class*="group"]') || element.parentElement;
    } catch (error) {
      console.error('❌ Error finding form container:', error);
      return element.parentElement;
    }
  }

  checkArabicValidation(inputElement) {
    try {
      if (!this.isArabicLanguage()) {
        return;
      }

      let value = inputElement.value;

      // Filter out numbers, punctuation, and unusual characters, keep only letters and spaces
      const lettersOnlyRegex = /[^a-zA-Z\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\s]/g;
      value = value.replace(lettersOnlyRegex, '');
      inputElement.value = value;

      // Validate Arabic characters and spaces only (no punctuation marks like : ; , .)
      const arabicRegex = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\s]+$/;

      if (value && !arabicRegex.test(value)) {
      } else {
        // Hide hint only if completely valid or empty
        this.hideArabicHint();
      }
    } catch (error) {
      console.error('❌ Error checking Arabic validation:', error);
    }
  }

  checkEnglishValidation(inputElement) {
    try {
      if (this.isArabicLanguage()) {
        return;
      }

      let value = inputElement.value;

      // Filter out Arabic characters, numbers, and punctuation, keep only Latin letters and spaces
      const latinOnlyRegex = /[^a-zA-ZÀ-ÿ\u00C0-\u017F\u0180-\u024F\u1E00-\u1EFF\u2C60-\u2C7F\uA720-\uA7FF\s]/g;
      value = value.replace(latinOnlyRegex, '');
      inputElement.value = value;

      const latinRegex = /^[a-zA-ZÀ-ÿ\u00C0-\u017F\u0180-\u024F\u1E00-\u1EFF\u2C60-\u2C7F\uA720-\uA7FF\s]+$/;

      // Check if there are Arabic characters in the input
      const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
      const hasArabicChars = arabicRegex.test(value);

      if (value && (hasArabicChars || !latinRegex.test(value))) {
      } else {
        // Hide hint only if completely valid or empty
        this.hideEnglishHint();
      }
    } catch (error) {
      console.error('❌ Error checking English validation:', error);
    }
  }

  addHintRepositionListeners(hintDiv, inputElement) {
    try {
      // Reposition on window resize
      const resizeHandler = () => {
        if (hintDiv && hintDiv.parentElement) {
          this.repositionHint(hintDiv, inputElement);
        }
      };

      // Reposition on scroll
      const scrollHandler = () => {
        if (hintDiv && hintDiv.parentElement) {
          this.repositionHint(hintDiv, inputElement);
        }
      };

      // Store handlers for cleanup
      hintDiv._resizeHandler = resizeHandler;
      hintDiv._scrollHandler = scrollHandler;

      window.addEventListener('resize', resizeHandler);
      window.addEventListener('scroll', scrollHandler, true);

    } catch (error) {
      console.error('❌ Error adding hint reposition listeners:', error);
    }
  }

  repositionHint(hintDiv, inputElement) {
    try {
      if (!inputElement || !hintDiv) return;

      const inputRect = inputElement.getBoundingClientRect();
      const hintHeight = 40;
      const spacing = 40; // Increased by 10px to match positioning

      let topPosition = inputRect.top - hintHeight - spacing;
      let leftPosition = inputRect.left;

      // Ensure hint stays within viewport
      if (topPosition < 10) {
        topPosition = 10;
      }

      if (leftPosition + inputRect.width > window.innerWidth) {
        leftPosition = window.innerWidth - inputRect.width - 10;
      }

      hintDiv.style.top = `${topPosition}px`;
      hintDiv.style.left = `${leftPosition}px`;
      hintDiv.style.width = `${inputRect.width}px`;

    } catch (error) {
      console.error('❌ Error repositioning hint:', error);
    }
  }

  filterInvalidCharacters(event) {
    try {
      const key = event.key;
      const keyCode = event.keyCode;

      // Allow: backspace, delete, tab, escape, enter, arrow keys, and space
      if ([8, 9, 27, 13, 32, 37, 38, 39, 40].indexOf(keyCode) !== -1) {
        return;
      }

      // Allow: letters (A-Z, a-z), Arabic characters, and space (no punctuation like : ; , .)
      const allowedChars = /[a-zA-Z\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\s]/;

      if (!allowedChars.test(key)) {
        event.preventDefault();
        return false;
      }

    } catch (error) {
      console.error('❌ Error filtering invalid characters:', error);
    }
  }

  filterEnglishCharacters(event) {
    try {
      const key = event.key;
      const keyCode = event.keyCode;

      // Allow: backspace, delete, tab, escape, enter, arrow keys, and space
      if ([8, 9, 27, 13, 32, 37, 38, 39, 40].indexOf(keyCode) !== -1) {
        return;
      }

      // Allow: Latin letters (A-Z, a-z, accented characters) and space (no punctuation like : ; , .)
      const allowedChars = /[a-zA-ZÀ-ÿ\u00C0-\u017F\u0180-\u024F\u1E00-\u1EFF\u2C60-\u2C7F\uA720-\uA7FF\s]/;

      if (!allowedChars.test(key)) {
        event.preventDefault();
        return false;
      }

    } catch (error) {
      console.error('❌ Error filtering Latin characters:', error);
    }
  }

  getArabicValidationMessage() {
    return window.themeStrings.common.arabicNameValidation;
  }

  getEnglishValidationMessage() {
    return window.themeStrings.common.englishNameValidation;
  }

  getChildNamePlaceholder() {
    return window.themeStrings.common.childNamePlaceholder;
  }

  getCurrentThemeLanguage() {
    try {
      // Method 1: Check Shopify.locale
      if (window.Shopify && window.Shopify.locale) {
        return window.Shopify.locale;
      }

      // Method 2: Check document.documentElement.lang
      if (document.documentElement.lang) {
        return document.documentElement.lang;
      }

      // Method 3: Check meta tag
      const langMeta = document.querySelector('meta[name="language"]');
      if (langMeta && langMeta.getAttribute('content')) {
        return langMeta.getAttribute('content');
      }

      // Method 4: Check html lang attribute
      const htmlLang = document.querySelector('html').getAttribute('lang');
      if (htmlLang) {
        return htmlLang;
      }

      // Method 5: Check URL path for language indicator
      const path = window.location.pathname;
      if (path.includes('/ar/') || path.includes('/ar-EG/')) {
        return 'ar';
      }

      // Method 6: Check for RTL direction (indicates Arabic)
      const isRTL = document.documentElement.dir === 'rtl' ||
        document.body.dir === 'rtl' ||
        getComputedStyle(document.documentElement).direction === 'rtl';
      if (isRTL) {
        return 'ar';
      }

      // Method 7: Check for Arabic text in the page
      const arabicTextRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
      if (arabicTextRegex.test(document.body.textContent)) {
        return 'ar';
      }

      // Default to English
      return 'en';
    } catch (error) {
      console.error('❌ Error detecting theme language:', error);
      return 'en';
    }
  }

  updateNameInputForLanguage() {
    try {
      const nameInput = document.querySelector('input[name="child-name"]');
      if (!nameInput) return;

      if (this.isArabicLanguage()) {
        // Update placeholder based on current theme language
        const placeholder = this.getChildNamePlaceholder();
        nameInput.setAttribute('placeholder', placeholder);
        nameInput.setAttribute('data-arabic-validation', 'true');

        // Re-setup Arabic validation
        this.setupArabicValidation();
      } else {
        // Reset to default for other languages
        const placeholder = this.getChildNamePlaceholder();
        nameInput.setAttribute('placeholder', placeholder);
        nameInput.removeAttribute('data-arabic-validation');
      }
    } catch (error) {
      console.error('❌ Error updating name input for language:', error);
    }
  }

  startShareTooltipTimer() {
    // Clear any existing tooltip timer
    if (this.shareTooltipTimer) {
      clearTimeout(this.shareTooltipTimer);
    }

    // Set timer to show tooltip after 8 seconds
    this.shareTooltipTimer = setTimeout(() => {
      this.showShareTooltip();
    }, 8000);

    // Add event listeners to hide tooltip on interactions
    this.setupTooltipEventListeners();
  }

  showShareTooltip() {
    try {
      const tooltipContainer = document.querySelector('.share-button-container');

      if (tooltipContainer) {
        // Update tooltip with kid name
        const kidName = this.formData.childName || this.getKidNameFromStoryData();
        const tooltipText = kidName
          ? themeFmt(window.themeStrings.previewer.shareTooltipNamed, { name: kidName })
          : window.themeStrings.previewer.shareTooltipGeneric;

        // Set the tooltip text and show it
        tooltipContainer.setAttribute('data-tooltip', tooltipText);
        tooltipContainer.classList.add('show-tooltip');

        // Auto-hide tooltip after 20 seconds
        setTimeout(() => {
          this.hideShareTooltip();
        }, 20000);
      }
    } catch (error) {
      console.error('Error showing share tooltip:', error);
    }
  }

  hideShareTooltip() {
    try {
      const tooltipContainer = document.querySelector('.share-button-container');
      if (tooltipContainer) {
        tooltipContainer.classList.remove('show-tooltip');
      }
    } catch (error) {
      console.error('Error hiding share tooltip:', error);
    }
  }

  getKidNameFromStoryData() {
    // Try to get kid name from various sources
    if (this.generatedData && this.generatedData.kid_name) {
      return this.generatedData.kid_name;
    }

    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const kidNameParam = urlParams.get('kid_name') || urlParams.get('kidName');
    if (kidNameParam) {
      return kidNameParam;
    }

    return null;
  }

  setupTooltipEventListeners() {
    // Hide tooltip when share button is clicked
    const shareButton = document.querySelector('.btn-share-header');
    if (shareButton && !shareButton.hasAttribute('data-tooltip-listener')) {
      shareButton.addEventListener('click', () => {
        this.hideShareTooltip();
      });
      shareButton.setAttribute('data-tooltip-listener', 'true');
    }

    // Hide tooltip when clicking outside
    document.addEventListener('click', (event) => {
      const shareContainer = document.querySelector('.share-button-container');

      if (shareContainer &&
        !shareContainer.contains(event.target) &&
        shareContainer.classList.contains('show-tooltip')) {
        this.hideShareTooltip();
      }
    });
  }

  // Temporary test function - can be called from browser console
  testShareTooltip() {

    this.showShareTooltip();
  }
}

// Global functions for step navigation
function nextStep() {
  if (window.previewerController) {
    window.previewerController.goToStep(window.previewerController.currentStep + 1);
  }
}

function previousStep() {
  if (window.previewerController) {
    window.previewerController.goToStep(window.previewerController.currentStep - 1);
  }
}

// Initialize the previewer when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.previewerController = new PreviewerController();
});

// Global test function for debugging
window.testTooltip = function () {
  if (window.previewerController) {
    window.previewerController.testShareTooltip();
  } else {
    console.error('PreviewerController not found');
  }
};